import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { mergeOppPools } from '../utils/tactic/teamScout';
import { motion } from 'framer-motion';
import { buildDraftIntel, scoreHeroForUs } from "../utils/draftIntel";
import heroList from '../data/heroList.json';
// ===== ที่ต้นไฟล์ DraftPage.jsx เพิ่มบรรทัดเหล่านี้ =====

// Components ใหม่
import SynergyCounterTooltip, { InlineSynergyBadge } from './draft/SynergyCounterViz';

// ✅ Hero Intel
import HeroIntelPanel from './draft/HeroIntelPanel';
import { buildHeroStats } from '../utils/heroStatsBuilder';
import './draft/fullsenseTheme.css';

// CSS Theme ใหม่
import './draft/draftComponentsTheme.css';
/* =========================
   LITE DRAFT PAGE
   - No Excel
   - No Tier
   - No Opponent Sheets
   - No Combo Engine
   - No Voice
   Focus: sequence/free, pre-choose, mark mode, history
   ========================= */

/* =========================
   Constants
   ========================= */
const BO_OPTIONS = { BO1: 1, BO3: 3, BO5: 5, BO7: 7 };
const highlightColor = '#39FF14';

const ROLE_ICON = {
  ทั้งหมด: '🌐',
  Fighter: '⚔️',
  Mage: '🪄',
  Assasin: '🗡️',
  Support: '💖',
  Tank: '🛡️',
  Carry: '🏹',
};
const ROLE_ORDER = ['Carry', 'Assasin', 'Mage', 'Fighter', 'Tank', 'Support'];
const OPP_POOL_POSITIONS = ['ALL', 'DSL', 'JUG', 'MID', 'ADL', 'SUP'];
const ALL_TEAMS_PAGE_SIZE = 4;
const OPP_POOL_LIMITS = {
  scanOneHero: 220,
  scanCombo2: 180,
  scanCombo3: 140,
  showOneHero: 14,
  showCombo: 12,
};
const EMPTY_OPP_POOL = { oneHero: [], combo2: [], combo3: [] };
const TEAM_TAG = { A: 'A', B: 'B' };
const ACTION_COLOR = { pick: '#ffffff', ban: '#d11a2a' };
const DRAFT_THEME = {
  surface: 'rgba(255,255,255,.84)',
  surfaceStrong: 'rgba(255,255,255,.94)',
  surfaceSoft: 'rgba(255,245,247,.90)',
  surfaceTint: 'rgba(255,16,38,.08)',
  border: 'rgba(255,16,38,.18)',
  borderStrong: 'rgba(255,16,38,.30)',
  text: '#7f1d1d',
  textStrong: '#991b1b',
  textMuted: '#6b7280',
  shadow: '0 12px 36px rgba(127,29,29,.10)',
};
const PICK_TEXT_COLOR = '#9f1239';

const LEFT_TEAM_ID = 'A';
const RIGHT_TEAM_ID = 'B';
const teamLabel = (teamId) => (teamId === 'A' ? 'ฝั่งซ้าย' : 'ฝั่งขวา');

const getImage = (f) => `${process.env.PUBLIC_URL}/heroimages/${f}`;

/* =========================
   Helpers
   ========================= */
function normalizeText(s) {
  return (s || '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
}
function normalizeRole(role) {
  const s = (role || '').toString().trim().toLowerCase();
  if (['assassin', 'assasin'].includes(s)) return 'Assasin';
  if (s === 'marksman' || s === 'carry') return 'Carry';
  if (s === 'tank') return 'Tank';
  if (s === 'fighter') return 'Fighter';
  if (s === 'mage') return 'Mage';
  if (s === 'support') return 'Support';
  return role || 'Unknown';
}

function normalizePositionFromGame(pos) {
  const p = String(pos || '').trim().toUpperCase();
  if (!p) return '';
  if (p === 'MIDLANE') return 'MID';
  if (p === 'ROAMING' || p === 'SUPPORT') return 'SUP';
  if (p === 'JUNGLE' || p === 'JGL') return 'JUG';
  if (['DSL', 'JUG', 'MID', 'ADL', 'SUP'].includes(p)) return p;
  return p;
}

function isCoreStatGame(g) {
  const t = String(g?.type || g?.Type || '').trim().toLowerCase();
  return t === 'rpl' || t === 'scrim';
}

function heroKey(name) {
  return String(name || '').trim().toLowerCase();
}

function hasPoolData(pack) {
  return ((pack?.oneHero?.length || 0) + (pack?.combo2?.length || 0) + (pack?.combo3?.length || 0)) > 0;
}

function findByNormalizedKey(obj, keyRaw) {
  const key = normalizeText(keyRaw);
  if (!key) return null;
  const entries = Object.entries(obj || {});
  const found = entries.find(([k]) => normalizeText(k) === key);
  return found ? found[1] : null;
}

function normalizeOneHeroToOppStats(rows = []) {
  const out = {};
  (rows || []).forEach((r) => {
    const hero = String(r?.hero || r?.name || '').trim();
    if (!hero) return;
    const total = Number(r?.total ?? r?.totalGames ?? 0) || 0;
    const winRaw = Number(r?.win ?? r?.wins ?? 0);
    const wrRaw = Number(r?.winrate ?? r?.winRate ?? 0);
    const win = Number.isFinite(winRaw)
      ? Math.max(0, Math.min(total, winRaw))
      : (Number.isFinite(wrRaw) ? Math.round((Math.max(0, wrRaw) / (wrRaw > 1 ? 100 : 1)) * total) : 0);

    out[hero] = {
      pick: total,
      ban: 0,
      win,
      lose: Math.max(0, total - win),
      winRate: total > 0 ? (win * 100) / total : 0,
    };
  });
  return out;
}

function getComboHeroes(comboRow) {
  if (Array.isArray(comboRow?.heroes)) return comboRow.heroes.map((h) => String(h || '').trim()).filter(Boolean);
  return String(comboRow?.combo || '')
    .split('+')
    .map((h) => String(h || '').trim())
    .filter(Boolean);
}

function findOneHeroStat(teamIntel, heroName) {
  const target = heroKey(heroName);
  if (!target) return null;
  const rows = Array.isArray(teamIntel?.oneHero) ? teamIntel.oneHero : [];
  const found = rows.filter((r) => heroKey(r?.hero) === target);
  if (!found.length) return null;
  return found.sort((a, b) => Number(b?.total || 0) - Number(a?.total || 0))[0];
}

function findActiveComboStat(teamIntel, heroName, currentPicks = []) {
  const target = heroKey(heroName);
  if (!target) return null;
  const pickSet = new Set((currentPicks || []).map(heroKey).filter(Boolean));
  pickSet.add(target);

  const allCombos = [
    ...(Array.isArray(teamIntel?.combo2) ? teamIntel.combo2 : []),
    ...(Array.isArray(teamIntel?.combo3) ? teamIntel.combo3 : []),
  ];

  const matched = allCombos
    .map((row) => {
      const heroes = getComboHeroes(row);
      const keys = heroes.map(heroKey);
      if (!keys.length) return null;
      if (!keys.includes(target)) return null;
      if (!keys.every((k) => pickSet.has(k))) return null;
      const total = Number(row?.total || 0);
      const win = Number(row?.win || 0);
      const winRate = total > 0 ? Number(((win / total) * 100).toFixed(1)) : 0;
      return {
        combo: row?.combo || heroes.join(' + '),
        total,
        win,
        winRate,
        size: heroes.length,
      };
    })
    .filter(Boolean);

  if (!matched.length) return null;
  return matched.sort((a, b) => (b.size - a.size) || (b.total - a.total) || (b.winRate - a.winRate))[0];
}

/* =========================
   Used Overlay
   ========================= */
function UsedOverlay({ mode = 'pick', faint = false }) {
  const color = mode === 'ban' ? ACTION_COLOR.ban : ACTION_COLOR.pick;
  const opacity = faint ? 0.6 : 1;
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity }}>
      <div
        style={{
          position: 'absolute',
          left: -8,
          right: -8,
          top: '50%',
          height: 4,
          transform: 'rotate(-36deg)',
          background: color,
          boxShadow: '0 0 8px #000',
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: -8,
          right: -8,
          top: '50%',
          height: 4,
          transform: 'rotate(36deg)',
          background: color, 
          boxShadow: '0 0 8px #000',
        }}
      />
    </div>
  );
}

/* =========================
   UI Atoms
   ========================= */
const btnGhost = {
  border: `1px solid ${DRAFT_THEME.border}`,
  borderRadius: 12,
  padding: '8px 12px',
  fontSize: 12,
  fontWeight: 900,
  cursor: 'pointer',
  background: DRAFT_THEME.surfaceStrong,
  color: DRAFT_THEME.textStrong,
  boxShadow: DRAFT_THEME.shadow,
};

function Pill({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        borderRadius: 999,
        border: `1px solid ${active ? DRAFT_THEME.borderStrong : DRAFT_THEME.border}`,
        background: active ? 'rgba(255,16,38,.12)' : 'rgba(255,255,255,.58)',
        color: active ? DRAFT_THEME.textStrong : DRAFT_THEME.text,
        padding: '6px 12px',
        fontSize: 12,
        fontWeight: 800,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

const PICK_ROLE_DEFAULT = 'default';
const PICK_ROLE_OPTIONS = ['DSL', 'ADL', 'MID', 'ROAM', 'JUG'];
const createDefaultPickRoles = () => ({
  A: Array(5).fill(PICK_ROLE_DEFAULT),
  B: Array(5).fill(PICK_ROLE_DEFAULT),
});
const SEQ_LR = [
  { team: 'L', count: 1, type: 'ban' },
  { team: 'R', count: 1, type: 'ban' },
  { team: 'L', count: 1, type: 'ban' },
  { team: 'R', count: 1, type: 'ban' },
  { team: 'L', count: 1, type: 'pick' },
  { team: 'R', count: 2, type: 'pick' },
  { team: 'L', count: 2, type: 'pick' },
  { team: 'R', count: 1, type: 'pick' },
  { team: 'R', count: 1, type: 'ban' },
  { team: 'L', count: 1, type: 'ban' },
  { team: 'R', count: 1, type: 'ban' },
  { team: 'L', count: 1, type: 'ban' },
  { team: 'R', count: 1, type: 'pick' },
  { team: 'L', count: 2, type: 'pick' },
  { team: 'R', count: 1, type: 'pick' },
];

/* =========================
   Main Component
   ========================= */
export default function DraftPage({
  heroes = heroList,
  games = [],
  comboStats = null,
  excelData = null,
  dropdownDates = [],
  excelFileBlob = null,
}) {
/* --- Synergy Tooltip State --- */
const [synergyTooltip, setSynergyTooltip] = useState(null);
// format: { hero: heroObject, x: number, y: number }
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1440
  );
  /* --- Hero Intel state --- */
  const [heroIntelPanel, setHeroIntelPanel] = useState(null);
  // heroIntelPanel = { hero, stats, perspective: 'us'|'them' }

  const closeHeroIntel = () => setHeroIntelPanel(null);

  /* --- Pre-choose --- */
  const [preChoose, setPreChoose] = useState({
    A: [null, null, null, null, null],
    B: [null, null, null, null, null],
  });
  const [pickRoles, setPickRoles] = useState(createDefaultPickRoles);
  const [selectingPre, setSelectingPre] = useState(null); // { team, i }
const [scrimStats, setScrimStats] = useState({}); // สรุปสถิติรายฮีโร่จาก scrim
const [banPickTotalGames, setBanPickTotalGames] = useState(0);
const [fsOneHeroFromRplStats, setFsOneHeroFromRplStats] = useState([]);
  const [allTeamsIntel, setAllTeamsIntel] = useState({}); // เก็บข้อมูลคู่แข่งทุกทีม
  const [opponentPoolTierData, setOpponentPoolTierData] = useState(EMPTY_OPP_POOL);
  // ชีท Specter_Scrim — เกมของทีมคู่แข่งที่เราส่องมา (ไม่มีเกมของ FS อยู่ในนั้น)
  // ปิดไว้ก่อนเพราะพูลนี้ป้อนคะแนนทำนายดราฟ ให้โคชเปิดเองเมื่อต้องการ
  const [scoutedPool, setScoutedPool] = useState(EMPTY_OPP_POOL);
  const [useScoutedPool, setUseScoutedPool] = useState(false);
  const specterRowsRef = useRef(null);
  /* --- Series --- */
  const [boType, setBoType] = useState(null);
  const [currentGame, setCurrentGame] = useState(1);
  const [globalPicks, setGlobalPicks] = useState({ A: [], B: [] });
  const [completedGames, setCompletedGames] = useState([]);
  const [viewingHistory, setViewingHistory] = useState(null);

  /* --- Draft (local game state) --- */
  const [stepIndex, setStepIndex] = useState(0);
  const [selectionCount, setSelectionCount] = useState(0);
  const [bans, setBans] = useState({ A: [], B: [] });
  const [picks, setPicks] = useState({ A: [], B: [] });
  const [history, setHistory] = useState([]);

  /* --- Filters/UI --- */
  const [alertMsg, setAlertMsg] = useState('');
  const [selectedRole, setSelectedRole] = useState('ทั้งหมด');
  const [searchTerm, setSearchTerm] = useState('');
  const [opponentPoolPos, setOpponentPoolPos] = useState('ALL');
  const [opponentPoolView, setOpponentPoolView] = useState('selected');
  const [isOpponentPoolOpen, setIsOpponentPoolOpen] = useState(false);
  const [allTeamsPage, setAllTeamsPage] = useState(1);
  const [poolSearchInput, setPoolSearchInput] = useState('');
  const [poolSearchKeyword, setPoolSearchKeyword] = useState('');
  const [poolSort, setPoolSort] = useState({
    one: { key: 'total', dir: 'desc' },
    combo: { key: 'total', dir: 'desc' },
  });

  /* --- Layout --- */
  const [banLayout, setBanLayout] = useState('horizontal');

  /* --- Modes --- */
  const [draftMode, setDraftMode] = useState('sequence'); // sequence | free
  const [freeAction, setFreeAction] = useState('pick'); // pick | ban

  /* --- Mark Mode --- */
  const [markMode, setMarkMode] = useState(false);
  const [markTool, setMarkTool] = useState('pickA');

  /* --- First/Second perspective --- */
  const [weAreFirst, setWeAreFirst] = useState(true);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const layoutPreset = useMemo(() => {
    const w = viewportWidth;

    if (w >= 1700) {
      return {
        sidePanelWidth: 178,
        sidePanelPadding: 18,
        centerPanelPadding: 24,
        centerPanelMarginX: 12,
        heroGridPairGap: 24,
        heroGridColumns: '1fr 1fr',
        heroRowGap: 17,
        heroCardSize: { width: 84, height: 108, imageHeight: 84, labelHeight: 24, radius: 16, fontSize: 12 },
      };
    }
    if (w >= 1366) {
      return {
        sidePanelWidth: 170,
        sidePanelPadding: 17,
        centerPanelPadding: 22,
        centerPanelMarginX: 12,
        heroGridPairGap: 22,
        heroGridColumns: '1fr 1fr',
        heroRowGap: 15,
        heroCardSize: { width: 82, height: 106, imageHeight: 82, labelHeight: 24, radius: 16, fontSize: 12 },
      };
    }
    if (w >= 1280) {
      return {
        sidePanelWidth: 150,
        sidePanelPadding: 14,
        centerPanelPadding: 18,
        centerPanelMarginX: 10,
        heroGridPairGap: 16,
        heroGridColumns: '1fr 1fr',
        heroRowGap: 12,
        heroCardSize: { width: 76, height: 100, imageHeight: 76, labelHeight: 24, radius: 14, fontSize: 11 },
      };
    }
    if (w >= 1024) {
      return {
        sidePanelWidth: 118,
        sidePanelPadding: 10,
        centerPanelPadding: 12,
        centerPanelMarginX: 6,
        heroGridPairGap: 12,
        heroGridColumns: '1fr 1fr',
        heroRowGap: 9,
        heroCardSize: { width: 70, height: 92, imageHeight: 70, labelHeight: 22, radius: 12, fontSize: 11 },
      };
    }
    if (w >= 820) {
      return {
        sidePanelWidth: 110,
        sidePanelPadding: 10,
        centerPanelPadding: 12,
        centerPanelMarginX: 6,
        heroGridPairGap: 10,
        heroGridColumns: '1fr',
        heroRowGap: 8,
        heroCardSize: { width: 68, height: 90, imageHeight: 68, labelHeight: 22, radius: 12, fontSize: 10 },
      };
    }
    return {
      sidePanelWidth: 94,
      sidePanelPadding: 8,
      centerPanelPadding: 10,
      centerPanelMarginX: 4,
      heroGridPairGap: 8,
      heroGridColumns: '1fr',
      heroRowGap: 7,
      heroCardSize: { width: 64, height: 84, imageHeight: 64, labelHeight: 20, radius: 10, fontSize: 10 },
    };
  }, [viewportWidth]);

  const {
    sidePanelWidth,
    sidePanelPadding,
    centerPanelPadding,
    centerPanelMarginX,
    heroGridPairGap,
    heroGridColumns,
    heroRowGap,
    heroCardSize,
  } = layoutPreset;
  const isTabletViewport = viewportWidth >= 820 && viewportWidth < 1366;
  const isCompactDraftLayout = viewportWidth < 1280;
  const horizontalBanIsStacked = viewportWidth < 1200;
  const banSectionColumns = horizontalBanIsStacked ? '1fr' : '1fr minmax(120px, 320px) 1fr';
  const banSectionSidePadding = horizontalBanIsStacked ? 0 : 24;
  const banSectionSideJustify = horizontalBanIsStacked ? 'flex-start' : 'flex-end';
  const filterSearchStyle = isCompactDraftLayout
    ? {
        flexBasis: '100%',
        width: '100%',
        marginLeft: 0,
      }
    : {
        marginLeft: 12,
        minWidth: 220,
      };

  const totalGames = boType ? BO_OPTIONS[boType] : 0;
  const isFinalGame = useMemo(
    () => !!boType && currentGame === BO_OPTIONS[boType],
    [boType, currentGame]
  );

  // มุมมอง "เรา/เค้า"
  const ourTeam = useMemo(() => (weAreFirst ? 'A' : 'B'), [weAreFirst]);
  const enemyTeam = useMemo(() => (ourTeam === 'A' ? 'B' : 'A'), [ourTeam]);
  const coreStatGames = useMemo(
    () => (Array.isArray(games) ? games.filter(isCoreStatGame) : []),
    [games]
  );

  const fsHeroMapFromRpl = useMemo(() => {
    const byHeroPos = new Map();
    (fsOneHeroFromRplStats || []).forEach((r) => {
      const hero = String(r?.hero || r?.name || '').trim();
      const position = String(r?.position || '').trim().toUpperCase();
      if (!hero) return;
      const total = Number(r?.total ?? r?.totalGames ?? 0) || 0;
      const winRaw = Number(r?.win ?? r?.wins ?? 0);
      const wrRaw = Number(r?.winrate ?? r?.winRate ?? 0);
      const win = Number.isFinite(winRaw) ? winRaw : (Number.isFinite(wrRaw) ? Math.round((total * wrRaw) / 100) : 0);
      const key = `${hero}|||${position}`;
      byHeroPos.set(key, {
        hero,
        position,
        pick: total,
        ban: 0,
        win: Math.max(0, Math.min(total, win)),
        lose: Math.max(0, total - Math.max(0, Math.min(total, win))),
      });
    });

    const map = {};
    byHeroPos.forEach((row) => {
      const cur = map[row.hero];
      // ใช้ตำแหน่งที่เล่นเยอะสุดของฮีโร่นั้น (แนวเดียวกับ TierData ที่แยกตาม role)
      if (!cur || Number(row.pick || 0) > Number(cur.pick || 0)) {
        map[row.hero] = { ...row };
      }
    });
    Object.keys(map).forEach((hero) => {
      const s = map[hero];
      const wl = (s.win || 0) + (s.lose || 0);
      map[hero] = {
        ...s,
        winRate: wl > 0 ? (s.win / wl) * 100 : 0,
      };
    });
    return map;
  }, [fsOneHeroFromRplStats]);

  const fsHeroMapFromGamesByPos = useMemo(() => {
    const byHeroPos = new Map();
    (coreStatGames || []).forEach((g) => {
      const isWin = Number(g?.win) === 1 || String(g?.result || '').toLowerCase() === 'win';
      const picksInGame = Array.isArray(g?.fsPick) ? g.fsPick : [];
      const posInGame = Array.isArray(g?.fsPosition) ? g.fsPosition : [];
      for (let i = 0; i < 5; i += 1) {
        const hero = String(picksInGame[i] || '').trim();
        const position = normalizePositionFromGame(posInGame[i]);
        if (!hero || !position) continue;
        const key = `${hero}|||${position}`;
        const cur = byHeroPos.get(key) || { hero, position, pick: 0, ban: 0, win: 0, lose: 0 };
        cur.pick += 1;
        cur.win += isWin ? 1 : 0;
        cur.lose += isWin ? 0 : 1;
        byHeroPos.set(key, cur);
      }
    });

    const out = {};
    byHeroPos.forEach((row) => {
      const cur = out[row.hero];
      if (!cur || Number(row.pick || 0) > Number(cur.pick || 0)) out[row.hero] = { ...row };
    });
    Object.keys(out).forEach((hero) => {
      const s = out[hero];
      const wl = (s.win || 0) + (s.lose || 0);
      out[hero] = { ...s, winRate: wl > 0 ? (s.win / wl) * 100 : 0 };
    });
    return out;
  }, [coreStatGames]);

  const oppByTeamFromSheets = useMemo(() => {
    const out = {};
    Object.entries(allTeamsIntel || {}).forEach(([team, data]) => {
      const one = Array.isArray(data?.oneHero) ? data.oneHero : [];
      const map = {};
      one.forEach((r) => {
        const hero = String(r?.hero || r?.name || '').trim();
        if (!hero) return;
        const total = Number(r?.total ?? r?.totalGames ?? 0) || 0;
        const winRaw = Number(r?.win ?? r?.wins ?? 0);
        const wrRaw = Number(r?.winrate ?? r?.winRate ?? 0);
        const win = Number.isFinite(winRaw) ? winRaw : (Number.isFinite(wrRaw) ? Math.round((total * wrRaw) / 100) : 0);
        map[hero] = {
          pick: total,
          ban: 0,
          win: Math.max(0, Math.min(total, win)),
          lose: Math.max(0, total - Math.max(0, Math.min(total, win))),
          winRate: total > 0 ? (Math.max(0, Math.min(total, win)) / total) * 100 : 0,
        };
      });
      out[team] = map;
    });
    return out;
  }, [allTeamsIntel]);

  // ----- Excel Intel -----
  const intel = useMemo(() => {
    const base = buildDraftIntel(coreStatGames);
    const fsMap = Object.keys(fsHeroMapFromRpl).length
      ? fsHeroMapFromRpl
      : (Object.keys(fsHeroMapFromGamesByPos).length ? fsHeroMapFromGamesByPos : (base?.fs || {}));
    const oppMap = Object.keys(oppByTeamFromSheets).length ? oppByTeamFromSheets : (base?.oppByTeam || {});

    // ใช้ global จากเกมจริง + เติม hero ที่มีใน fs map แต่ไม่อยู่ใน global
    const global = { ...(base?.global || {}) };
    Object.entries(fsMap).forEach(([hero, row]) => {
      if (!global[hero]) global[hero] = { ...row };
    });

    return {
      ...base,
      global,
      fs: fsMap,
      oppByTeam: oppMap,
      teams: Object.keys(oppMap || {}).filter(Boolean).sort(),
      scrimStats,
      // ใช้ฐานเดียวกับสถิติจริงของเราเป็นหลัก
      heroStats: fsMap,
      totalGames: banPickTotalGames || coreStatGames.length || 0,
    };
  }, [coreStatGames, scrimStats, banPickTotalGames, fsHeroMapFromRpl, fsHeroMapFromGamesByPos, oppByTeamFromSheets]);

  const [opponent, setOpponent] = useState("");
  useEffect(() => {
    if (!opponent && intel?.teams?.length) setOpponent(intel.teams[0]);
  }, [intel, opponent]);

  // พูลคู่แข่งที่ใช้จริง = ชีทสรุป (+ ชีทส่อง ถ้าเปิด)
  const oppPool = useMemo(() => {
    if (!useScoutedPool || !scoutedPool?.games) return opponentPoolTierData;
    return mergeOppPools(opponentPoolTierData, scoutedPool);
  }, [useScoutedPool, scoutedPool, opponentPoolTierData]);

  const selectedOpponentSheetIntel = useMemo(() => {
    const tierPack = oppPool || EMPTY_OPP_POOL;
    if (hasPoolData(tierPack)) return tierPack;
    const fromSheet = findByNormalizedKey(allTeamsIntel, opponent);
    return fromSheet || null;
  }, [oppPool, allTeamsIntel, opponent]);

  const selectedOpponentFallbackStats = useMemo(
    () => findByNormalizedKey(intel?.oppByTeam || {}, opponent) || {},
    [intel, opponent]
  );

  const selectedOpponentIntelPack = useMemo(() => {
    if (selectedOpponentSheetIntel) return selectedOpponentSheetIntel;
    const oneHero = Object.entries(selectedOpponentFallbackStats || {})
      .map(([hero, s]) => {
        const total = Number(s?.pick || 0);
        const win = Math.max(0, Math.min(total, Number(s?.win || 0)));
        return {
          hero: String(hero || '').trim(),
          position: '',
          total,
          win,
          winRate: total > 0 ? +((win * 100) / total).toFixed(1) : 0,
        };
      })
      .filter((r) => r.hero && r.total > 0);

    if (!oneHero.length) return null;
    return { oneHero, combo2: [], combo3: [] };
  }, [selectedOpponentSheetIntel, selectedOpponentFallbackStats]);

  const selectedOpponentStatsFromPool = useMemo(
    () => normalizeOneHeroToOppStats(selectedOpponentIntelPack?.oneHero || []),
    [selectedOpponentIntelPack]
  );

  const intelForDraftTools = useMemo(() => {
    if (!intel || !opponent) return intel;
    return {
      ...intel,
      oppByTeam: {
        ...(intel?.oppByTeam || {}),
        [opponent]: selectedOpponentStatsFromPool,
      },
    };
  }, [intel, opponent, selectedOpponentStatsFromPool]);

  /* =========================
     Hero Intel helpers
     ========================= */
  const makeHeroStatsForPanel = (hero, viewTeam) => {
    const isOurSide = viewTeam === ourTeam;
    const perspective = isOurSide ? 'us' : 'them';
    const teamIntel = !isOurSide ? selectedOpponentIntelPack : null;

    // ใช้ intel ที่มีอยู่ + side/pos
    // buildHeroStats รองรับ missing บาง field ได้ (จะ fallback ให้)
    let stats = buildHeroStats(hero, {
      side: perspective === 'us' ? 'fs' : 'op',
      position: normalizeRole(hero.role),
      intel: intelForDraftTools || null,
      fsData: null,
      opponent: opponent || null,
    });

    const oneHero = findOneHeroStat(teamIntel, hero?.name);
    if (oneHero) {
      const total = Number(oneHero.total || 0);
      const win = Number(oneHero.win || 0);
      const winRate = total > 0 ? Number(((win / total) * 100).toFixed(1)) : Number(stats.winRate || 0);
      stats = {
        ...stats,
        winRate,
        totalGames: total,
      };
    }

    const picksNow = (picks?.[viewTeam] || []).map((h) => h?.name).filter(Boolean);
    const comboInsight = findActiveComboStat(teamIntel, hero?.name, picksNow);
    if (comboInsight) {
      stats = {
        ...stats,
        comboInsight,
      };
    }

    stats = {
      ...stats,
      name: hero?.name || stats?.name || 'Unknown',
      role: normalizeRole(hero?.role || stats?.role || ''),
    };

    return { stats, perspective };
  };

  const openHeroIntelPanel = (hero, viewTeam) => {
    const { stats, perspective } = makeHeroStatsForPanel(hero, viewTeam);
    setHeroIntelPanel({ hero, stats, perspective });
  };

  /* =========================
     Fixed Left-first sequence
     ========================= */
  const draftSeq = useMemo(() => {
    const L = LEFT_TEAM_ID;
    const R = RIGHT_TEAM_ID;
    return SEQ_LR.map((s) => ({ ...s, team: s.team === 'L' ? L : R }));
  }, []);

  const totalSteps = draftSeq.length;
  const currentStep = useMemo(() => draftSeq[stepIndex] || {}, [draftSeq, stepIndex]);
  const { team: highlightTeamSeq, type: highlightTypeSeq, count: highlightCountSeq } =
    currentStep;

  const highlightTeam = draftMode === 'free' ? null : highlightTeamSeq;
  const highlightType = draftMode === 'free' ? null : highlightTypeSeq;
  const highlightCount = draftMode === 'free' ? 0 : highlightCountSeq;

  const highlightStartIndex = useMemo(
    () =>
      draftSeq
        .slice(0, stepIndex)
        .filter((s) => s.team === highlightTeamSeq && s.type === highlightTypeSeq)
        .reduce((sum, s) => sum + s.count, 0),
    [draftSeq, stepIndex, highlightTeamSeq, highlightTypeSeq]
  );

  /* =========================
     Swap state when FS/Second
     ========================= */
  function swapTeamsState() {
    setPicks((p) => ({ A: [...p.B], B: [...p.A] }));
    setBans((b) => ({ A: [...b.B], B: [...b.A] }));
    setPreChoose((pc) => ({ A: [...pc.B], B: [...pc.A] }));
    setPickRoles((pr) => ({ A: [...pr.B], B: [...pr.A] }));
    setGlobalPicks((g) => ({ A: [...g.B], B: [...g.A] }));
    setHistory((h) => h.map((x) => ({ ...x, team: x.team === 'A' ? 'B' : 'A' })));
  }

  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    swapTeamsState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weAreFirst]);

  /* =========================
     Derived lists
     ========================= */
  const roleList = useMemo(
    () => ['ทั้งหมด', ...ROLE_ORDER.filter((r) => heroes.some((h) => normalizeRole(h.role) === r))],
    [heroes]
  );

  const filteredHeroList = useMemo(() => {
    let list =
      selectedRole === 'ทั้งหมด'
        ? heroes
        : heroes.filter((h) => normalizeRole(h.role) === selectedRole);

    if (searchTerm.trim()) {
      const term = normalizeText(searchTerm);
      list = list.filter((h) => normalizeText(h.name).includes(term));
    }
    return list;
  }, [heroes, selectedRole, searchTerm]);

  const heroesByRole = useMemo(() => {
    const map = {};
    ROLE_ORDER.forEach((r) => (map[r] = []));
    filteredHeroList.forEach((h) => {
      const r = normalizeRole(h.role);
      if (!map[r]) map[r] = [];
      map[r].push(h);
    });
    return map;
  }, [filteredHeroList]);

  const heroByName = useMemo(() => {
    const map = {};
    (heroes || []).forEach((h) => {
      if (h?.name) map[h.name] = h;
    });
    return map;
  }, [heroes]);

  const stickyThStyle = {
    position: 'sticky',
    top: 0,
    background: 'rgba(8,10,18,.95)',
    backdropFilter: 'blur(2px)',
    zIndex: 2,
  };

  const onPoolSort = useCallback((scope, key) => {
    setPoolSort((prev) => {
      const cur = prev?.[scope] || { key: 'total', dir: 'desc' };
      if (cur.key === key) {
        return {
          ...prev,
          [scope]: { key, dir: cur.dir === 'desc' ? 'asc' : 'desc' },
        };
      }
      return {
        ...prev,
        [scope]: { key, dir: 'desc' },
      };
    });
  }, []);

  const sortMark = useCallback((scope, key) => {
    const cur = poolSort?.[scope];
    if (!cur || cur.key !== key) return '';
    return cur.dir === 'desc' ? ' ▼' : ' ▲';
  }, [poolSort]);

  const renderHeroAvatar = (heroName, size = 20) => {
    const key = String(heroName || '').trim();
    const hero = heroByName[key];
    const img = hero?.image ? getImage(hero.image) : null;
    return (
      img ? (
        <img
          src={img}
          alt={key}
          style={{
            width: size,
            height: size,
            borderRadius: 6,
            objectFit: 'cover',
            border: '1px solid rgba(255,255,255,.18)',
            flexShrink: 0,
          }}
        />
      ) : (
        <div
          style={{
            width: size,
            height: size,
            borderRadius: 6,
            background: 'rgba(255,255,255,.10)',
            border: '1px solid rgba(255,255,255,.14)',
            flexShrink: 0,
          }}
        />
      )
    );
  };

  const renderComboCell = (combo, size = 18) => {
    const heroesInCombo = String(combo || '')
      .split('+')
      .map((h) => String(h || '').trim())
      .filter(Boolean)
      .slice(0, 3);

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginRight: 2 }}>
          {heroesInCombo.map((h, i) => {
            const hero = heroByName[h];
            const img = hero?.image ? getImage(hero.image) : null;
            return (
              <div
                key={`${combo}-${h}-${i}`}
                style={{
                  width: size,
                  height: size,
                  marginLeft: i === 0 ? 0 : -5,
                  borderRadius: 999,
                  overflow: 'hidden',
                  border: '1px solid rgba(255,255,255,.24)',
                  background: 'rgba(255,255,255,.12)',
                  boxShadow: '0 2px 6px rgba(0,0,0,.25)',
                  flexShrink: 0,
                }}
              >
                {img && <img src={img} alt={h} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
              </div>
            );
          })}
        </div>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{combo}</span>
      </div>
    );
  };

  const localUsedNames = useMemo(
    () => [...bans.A, ...bans.B, ...picks.A, ...picks.B].map((h) => h.name),
    [bans, picks]
  );

  const bannedNamesSet = useMemo(() => {
    const s = new Set();
    (bans.A || []).forEach((h) => h?.name && s.add(h.name));
    (bans.B || []).forEach((h) => h?.name && s.add(h.name));
    return s;
  }, [bans]);

  const pickedNamesSet = useMemo(() => {
    const s = new Set();
    (picks.A || []).forEach((h) => h?.name && s.add(h.name));
    (picks.B || []).forEach((h) => h?.name && s.add(h.name));
    return s;
  }, [picks]);

  const bestForUs = useMemo(() => {
    if (!intelForDraftTools) return [];
    const list = Array.from(new Set((heroes || []).map((h) => h?.name).filter(Boolean)))
      .filter((name) => !bannedNamesSet.has(name) && !pickedNamesSet.has(name));

    const isFinalBO7 = boType === 'BO7' && currentGame === BO_OPTIONS.BO7;
    const blockedByGlobal = (name) => (!isFinalBO7 && globalPicks[ourTeam]?.includes(name));

    const scored = list
      .filter((name) => !blockedByGlobal(name))
      .map((name) => scoreHeroForUs({ hero: name, intel: intelForDraftTools, opponent }))
      .filter(Boolean)
      .sort((a, b) => b.score - a.score)
      .slice(0, 8);

    return scored;
  }, [heroes, intelForDraftTools, opponent, bannedNamesSet, pickedNamesSet, boType, currentGame, globalPicks, ourTeam]);

  const isEnemyTurn = useMemo(() => {
    if (draftMode !== "sequence") return false;
    if (!currentStep?.team || !currentStep?.type) return false;
    return currentStep.team === enemyTeam;
  }, [draftMode, currentStep, enemyTeam]);

  const usedHeroKeySet = useMemo(
    () => new Set(localUsedNames.map(heroKey).filter(Boolean)),
    [localUsedNames]
  );

  const fallbackOppByTeam = useMemo(
    () => (intel?.oppByTeam || {}),
    [intel]
  );

  const fallbackOppByTeamNormalized = useMemo(() => {
    const map = new Map();
    Object.entries(fallbackOppByTeam).forEach(([team, stats]) => {
      map.set(normalizeText(team), stats || {});
    });
    return map;
  }, [fallbackOppByTeam]);

  const allTeamsIntelNormalized = useMemo(() => {
    const map = new Map();
    Object.entries(allTeamsIntel || {}).forEach(([team, data]) => {
      map.set(normalizeText(team), data || {});
    });
    return map;
  }, [allTeamsIntel]);

  const getTeamIntelPack = useCallback((teamName) => {
    const key = normalizeText(teamName);
    const oppKey = normalizeText(opponent);
    const fromTierData = key === oppKey ? (oppPool || null) : null;
    const hasTierData =
      ((fromTierData?.oneHero?.length || 0) +
      (fromTierData?.combo2?.length || 0) +
      (fromTierData?.combo3?.length || 0)) > 0;
    const fromSheet = hasTierData ? fromTierData : (allTeamsIntelNormalized.get(key) || null);
    const fromFallback = fallbackOppByTeamNormalized.get(key) || {};
    return { fromSheet, fromFallback };
  }, [allTeamsIntelNormalized, fallbackOppByTeamNormalized, opponent, oppPool]);

  const allOpponentTeams = useMemo(() => {
    return Array.from(new Set([
      ...((intel?.teams || []).filter(Boolean)),
      ...Object.keys(allTeamsIntel || {}).filter(Boolean),
    ])).sort((a, b) => a.localeCompare(b));
  }, [intel, allTeamsIntel]);

  const teamPoolSeedMap = useMemo(() => {
    const map = new Map();

    allOpponentTeams.forEach((teamName) => {
      const { fromSheet, fromFallback } = getTeamIntelPack(teamName);
      const baseOne = Array.isArray(fromSheet?.oneHero) ? fromSheet.oneHero : [];
      const fallbackOne = Object.entries(fromFallback || {})
        .map(([hero, s]) => {
          const total = Number(s?.pick || 0);
          const win = Number(s?.win || 0);
          const clampedWin = Math.max(0, Math.min(total, win));
          return {
            hero: String(hero || '').trim(),
            position: '',
            total,
            win: clampedWin,
            winRate: total > 0 ? +((clampedWin * 100) / total).toFixed(1) : 0,
          };
        })
        .filter((r) => r.hero && r.total > 0);

      const sourceOne = baseOne.length ? baseOne : fallbackOne;
      const oneHeroSeed = sourceOne
        .map((r) => {
          const hero = String(r?.hero || r?.name || '').trim();
          const position = String(r?.position || '').trim().toUpperCase();
          const total = Number(r?.total ?? r?.totalGames ?? 0) || 0;
          const winRaw = Number(r?.win ?? r?.wins ?? 0);
          const wrRaw = Number(r?.winrate ?? r?.winRate ?? 0);
          const win = Number.isFinite(winRaw)
            ? Math.max(0, Math.min(total, winRaw))
            : (Number.isFinite(wrRaw) ? Math.round((Math.max(0, wrRaw) / (wrRaw > 1 ? 100 : 1)) * total) : 0);
          const winRate = total > 0 ? +((win * 100) / total).toFixed(1) : 0;
          return { hero, position, total, win, winRate };
        })
        .filter((r) => r.hero && r.total > 0)
        .sort((a, b) => (b.total - a.total) || (b.winRate - a.winRate) || a.hero.localeCompare(b.hero))
        .slice(0, OPP_POOL_LIMITS.scanOneHero);

      const combo2Seed = (Array.isArray(fromSheet?.combo2) ? fromSheet.combo2 : [])
        .map((row) => {
          const heroes = getComboHeroes(row).map((h) => String(h || '').trim()).filter(Boolean);
          if (heroes.length !== 2) return null;
          const total = Number(row?.total || 0);
          const win = Number(row?.win || 0);
          const safeWin = Math.max(0, Math.min(total, win));
          const winRate = total > 0 ? +((safeWin * 100) / total).toFixed(1) : 0;
          return {
            combo: row?.combo || heroes.join(' + '),
            heroes,
            comboType: String(row?.comboType || '').toUpperCase(),
            total,
            win: safeWin,
            winRate,
          };
        })
        .filter(Boolean)
        .sort((a, b) => (b.total - a.total) || (b.winRate - a.winRate))
        .slice(0, OPP_POOL_LIMITS.scanCombo2);

      const combo3Seed = (Array.isArray(fromSheet?.combo3) ? fromSheet.combo3 : [])
        .map((row) => {
          const heroes = getComboHeroes(row).map((h) => String(h || '').trim()).filter(Boolean);
          if (heroes.length !== 3) return null;
          const total = Number(row?.total || 0);
          const win = Number(row?.win || 0);
          const safeWin = Math.max(0, Math.min(total, win));
          const winRate = total > 0 ? +((safeWin * 100) / total).toFixed(1) : 0;
          return {
            combo: row?.combo || heroes.join(' + '),
            heroes,
            comboType: String(row?.comboType || '').toUpperCase(),
            total,
            win: safeWin,
            winRate,
          };
        })
        .filter(Boolean)
        .sort((a, b) => (b.total - a.total) || (b.winRate - a.winRate))
        .slice(0, OPP_POOL_LIMITS.scanCombo3);

      map.set(normalizeText(teamName), { oneHeroSeed, combo2Seed, combo3Seed });
    });

    return map;
  }, [allOpponentTeams, getTeamIntelPack]);

  const buildPoolRowsByTeamName = useCallback((teamName = '') => {
    const seed = teamPoolSeedMap.get(normalizeText(teamName)) || {
      oneHeroSeed: [],
      combo2Seed: [],
      combo3Seed: [],
    };

    const filteredOne = seed.oneHeroSeed
      .filter((r) => {
        if (usedHeroKeySet.has(heroKey(r.hero))) return false;
        if (opponentPoolPos !== 'ALL' && r.position !== opponentPoolPos) return false;
        if (poolSearchKeyword && !String(r.hero || '').toLowerCase().includes(poolSearchKeyword)) return false;
        return true;
      });
    const totalPicks = filteredOne.reduce((sum, r) => sum + (r.total || 0), 0);
    const oneHeroRowsBase = filteredOne.slice(0, OPP_POOL_LIMITS.showOneHero).map((r) => ({
      ...r,
      pickRate: totalPicks > 0 ? +(((r.total || 0) * 100) / totalPicks).toFixed(2) : 0,
    }));

    const combo2RowsBase = seed.combo2Seed
      .filter((r) => {
        if (r.heroes.some((h) => usedHeroKeySet.has(heroKey(h)))) return false;
        if (opponentPoolPos !== 'ALL' && !r.comboType.includes(opponentPoolPos)) return false;
        if (poolSearchKeyword && !String(r.combo || '').toLowerCase().includes(poolSearchKeyword)) return false;
        return true;
      })
      .slice(0, OPP_POOL_LIMITS.showCombo)
      .map((r) => ({ combo: r.combo, total: r.total, win: r.win, winRate: r.winRate }));

    const combo3RowsBase = seed.combo3Seed
      .filter((r) => {
        if (r.heroes.some((h) => usedHeroKeySet.has(heroKey(h)))) return false;
        if (opponentPoolPos !== 'ALL' && !r.comboType.includes(opponentPoolPos)) return false;
        if (poolSearchKeyword && !String(r.combo || '').toLowerCase().includes(poolSearchKeyword)) return false;
        return true;
      })
      .slice(0, OPP_POOL_LIMITS.showCombo)
      .map((r) => ({ combo: r.combo, total: r.total, win: r.win, winRate: r.winRate }));

    const oneOrder = poolSort?.one || { key: 'total', dir: 'desc' };
    const comboOrder = poolSort?.combo || { key: 'total', dir: 'desc' };
    const orderMulOne = oneOrder.dir === 'asc' ? 1 : -1;
    const orderMulCombo = comboOrder.dir === 'asc' ? 1 : -1;
    const oneHeroRows = [...oneHeroRowsBase].sort((a, b) => {
      const av = Number(a?.[oneOrder.key] || 0);
      const bv = Number(b?.[oneOrder.key] || 0);
      if (av !== bv) return (av - bv) * orderMulOne;
      return String(a?.hero || '').localeCompare(String(b?.hero || ''));
    });
    const comboSorter = (a, b) => {
      const av = Number(a?.[comboOrder.key] || 0);
      const bv = Number(b?.[comboOrder.key] || 0);
      if (av !== bv) return (av - bv) * orderMulCombo;
      return String(a?.combo || '').localeCompare(String(b?.combo || ''));
    };
    const combo2Rows = [...combo2RowsBase].sort(comboSorter);
    const combo3Rows = [...combo3RowsBase].sort(comboSorter);

    return { oneHeroRows, combo2Rows, combo3Rows };
  }, [teamPoolSeedMap, usedHeroKeySet, opponentPoolPos, poolSearchKeyword, poolSort]);

  const selectedOpponentPoolPack = useMemo(() => {
    return buildPoolRowsByTeamName(opponent);
  }, [opponent, buildPoolRowsByTeamName]);
  const opponentOneHeroRows = selectedOpponentPoolPack.oneHeroRows;
  const opponentCombo2Rows = selectedOpponentPoolPack.combo2Rows;
  const opponentCombo3Rows = selectedOpponentPoolPack.combo3Rows;

  const enemyPredictionList = useMemo(() => {
    if (!isEnemyTurn || !opponent) return [];
    const topRows = [...(opponentOneHeroRows || [])]
      .sort((a, b) => Number(b?.total || 0) - Number(a?.total || 0))
      .slice(0, 5);
    const maxPickRate = Math.max(0.01, ...topRows.map((r) => Number(r?.pickRate || 0)));
    const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

    return topRows.map((r) => ({
      total: Number(r?.total || 0),
      winRate: Number(r?.winRate || 0),
      hero: String(r?.hero || '').trim(),
      pct: Number(r?.pickRate || 0),
      // Calibrated to be comparable with "Our Best Picks" score range.
      score: Math.round(clamp(
        50
          + (Number(r?.pickRate || 0) / maxPickRate) * 12
          + (Number(r?.winRate || 0) - 50) * 0.30,
        35,
        75
      )),
      reason: [
        `อิง Opponent Pool ของ ${opponent}`,
        `PR ${Number(r?.pickRate || 0).toFixed(2)}% • WR ${Number(r?.winRate || 0).toFixed(1)}%`,
      ],
    }));
  }, [isEnemyTurn, opponent, opponentOneHeroRows]);

  const opponentPoolByTeams = useMemo(() => {
    if (opponentPoolView !== 'allTeams') return [];
    const teamsForView = allOpponentTeams;

    return teamsForView.map((team) => {
      const pack = buildPoolRowsByTeamName(team);
      return {
        team,
        oneHeroRows: pack.oneHeroRows,
        combo2Rows: pack.combo2Rows,
        combo3Rows: pack.combo3Rows,
      };
    });
  }, [opponentPoolView, allOpponentTeams, buildPoolRowsByTeamName]);

  const totalAllTeamsPages = useMemo(() => {
    return Math.max(1, Math.ceil(opponentPoolByTeams.length / ALL_TEAMS_PAGE_SIZE));
  }, [opponentPoolByTeams.length]);

  useEffect(() => {
    setAllTeamsPage(1);
  }, [opponentPoolView, opponentPoolPos, poolSearchKeyword]);

  useEffect(() => {
    if (allTeamsPage > totalAllTeamsPages) setAllTeamsPage(totalAllTeamsPages);
  }, [allTeamsPage, totalAllTeamsPages]);

  const visibleOpponentPoolByTeams = useMemo(() => {
    if (opponentPoolView !== 'allTeams') return [];
    const start = (allTeamsPage - 1) * ALL_TEAMS_PAGE_SIZE;
    return opponentPoolByTeams.slice(start, start + ALL_TEAMS_PAGE_SIZE);
  }, [opponentPoolView, allTeamsPage, opponentPoolByTeams]);

  /* =========================
     Rules
     ========================= */
  const isFlowbornHero = (heroName) => /^flowborn(?:\s|-)/i.test(normalizeText(heroName));

  const teamHasFlowbornPick = (team) =>
    (picks[team] || []).some((h) => isFlowbornHero(h?.name));

  const teamHasFlowbornPreChoose = (team, exceptIdx = -1) =>
    (preChoose[team] || []).some((h, idx) => idx !== exceptIdx && isFlowbornHero(h?.name));

  const isFlowbornBlockedForTeam = (team, heroName, exceptPreChooseIdx = -1) =>
    isFlowbornHero(heroName) && (teamHasFlowbornPick(team) || teamHasFlowbornPreChoose(team, exceptPreChooseIdx));

  function canBan(team, heroName) {
    if ((bans[team] || []).length >= 4) {
      return { ok: false, msg: `${team} แบนครบ 4 แล้ว` };
    }
    if (localUsedNames.includes(heroName)) {
      return { ok: false, msg: `"${heroName}" ถูกใช้/แบนในเกมนี้แล้ว` };
    }
    return { ok: true };
  }

  function canPick(team, heroName, options = {}) {
    if (localUsedNames.includes(heroName)) {
      return { ok: false, msg: `"${heroName}" ถูกใช้/แบนในเกมนี้แล้ว` };
    }
    const isFinalBO7 = boType === 'BO7' && currentGame === BO_OPTIONS.BO7;
    if (!isFinalBO7 && globalPicks[team]?.includes(heroName)) {
      return { ok: false, msg: `กฎ Global: ${team} เลือก "${heroName}" ไปแล้วในเกมก่อนหน้า` };
    }
    if (isFlowbornBlockedForTeam(team, heroName, options.exceptPreChooseIdx ?? -1)) {
      return { ok: false, msg: `${team} เลือก Flowborn ได้แค่ 1 ตัวต่อเกม` };
    }
    if ((picks[team] || []).length >= 5) {
      return { ok: false, msg: `${team} เลือกครบ 5 แล้ว` };
    }
    return { ok: true };
  }

  function canPreChoose(team, pickIdx, heroName) {
    if (localUsedNames.includes(heroName)) {
      return { ok: false, msg: `"${heroName}" ถูกใช้/แบนในเกมนี้แล้ว` };
    }
    if (isFlowbornBlockedForTeam(team, heroName, pickIdx)) {
      return { ok: false, msg: `${team} วางแผน Flowborn ได้แค่ 1 ตัวต่อเกม` };
    }
    return { ok: true };
  }

  /* =========================
     Actions
     ========================= */
  function handlePreChoose(team, pickIdx, hero) {
    const check = canPreChoose(team, pickIdx, hero.name);
    if (!check.ok) {
      setAlertMsg(check.msg);
      return;
    }
    setPreChoose((prev) => {
      const arr = [...prev[team]];
      const dup = arr.findIndex((h) => h?.name === hero.name);
      if (dup !== -1) arr[dup] = null;
      arr[pickIdx] = hero;
      return { ...prev, [team]: arr };
    });
  }

  function handleClearPreChoose(team, pickIdx) {
    setPreChoose((prev) => {
      const arr = [...prev[team]];
      arr[pickIdx] = null;
      return { ...prev, [team]: arr };
    });
  }

  function setPickRole(team, pickIdx, role) {
    setPickRoles((prev) => {
      const arr = [...prev[team]];
      arr[pickIdx] = role;
      return { ...prev, [team]: arr };
    });
  }

  function doFreeAction(hero, team, type) {
    if (type === 'ban') {
      const check = canBan(team, hero.name);
      if (!check.ok) {
        setAlertMsg(check.msg);
        return;
      }
      setBans((p) => ({ ...p, [team]: [...p[team], hero] }));
      setHistory((h) => [...h, { stepIndex: -1, team, hero, type }]);
      setAlertMsg('');
      return;
    }

    const pickIdx = picks[team].length;
    const check = canPick(team, hero.name, { exceptPreChooseIdx: pickIdx });
    if (!check.ok) {
      setAlertMsg(check.msg);
      return;
    }

    setPicks((p) => ({ ...p, [team]: [...p[team], hero] }));

    const isFinalBO7 = boType === 'BO7' && currentGame === BO_OPTIONS.BO7;
    if (!isFinalBO7) {
      setGlobalPicks((g) => ({ ...g, [team]: [...g[team], hero.name] }));
    }

    if (preChoose[team][pickIdx]) handleClearPreChoose(team, pickIdx);

    setHistory((h) => [...h, { stepIndex: -1, team, hero, type }]);
    setAlertMsg('');
  }

  function handleHeroClick(hero) {
    if (selectingPre) return;
    if (draftMode === 'free') return;
    if (stepIndex >= totalSteps || !currentStep?.type || !currentStep?.team) return;

    const { team, type, count } = currentStep;
    const pickIdx = picks[team].length;
    const check = type === 'ban' ? canBan(team, hero.name) : canPick(team, hero.name, { exceptPreChooseIdx: pickIdx });
    if (!check.ok) {
      setAlertMsg(check.msg);
      return;
    }

    if (type === 'ban') {
      setBans((p) => ({ ...p, [team]: [...p[team], hero] }));
    } else {
      setPicks((p) => ({ ...p, [team]: [...p[team], hero] }));
      if (!isFinalGame) {
        setGlobalPicks((g) => ({ ...g, [team]: [...g[team], hero.name] }));
      }
      if (preChoose[team][pickIdx]) handleClearPreChoose(team, pickIdx);
    }

    setHistory((h) => [...h, { stepIndex, team, hero, type }]);
    setAlertMsg('');

    const nextCount = selectionCount + 1;
    if (nextCount >= count) {
      setStepIndex((i) => i + 1);
      setSelectionCount(0);
    } else {
      setSelectionCount(nextCount);
    }
  }

  function markPickOrBan(hero) {
    if (!markMode || !hero) return;

    let team = 'A', action = 'pick';
    if (markTool === 'pickA') { team = 'A'; action = 'pick'; }
    if (markTool === 'pickB') { team = 'B'; action = 'pick'; }
    if (markTool === 'banA')  { team = 'A'; action = 'ban'; }
    if (markTool === 'banB')  { team = 'B'; action = 'ban'; }

    setDraftMode('free');
    setFreeAction(action);
    doFreeAction(hero, team, action);
  }

  function handleUndo() {
    if (!history.length) return;
    const last = history[history.length - 1];
    const { stepIndex: lastStep, team, hero, type } = last;

    if (type === 'ban') {
      setBans((p) => ({ ...p, [team]: p[team].filter((h) => h.name !== hero.name) }));
    } else {
      setPicks((p) => ({ ...p, [team]: p[team].filter((h) => h.name !== hero.name) }));
      if (globalPicks[team].includes(hero.name)) {
        setGlobalPicks((g) => ({ ...g, [team]: g[team].filter((n) => n !== hero.name) }));
      }
    }

    setHistory((h) => {
      const newH = h.slice(0, -1);
      if (draftMode === 'sequence') {
        const countInThatStep = newH.filter((a) => a.stepIndex === lastStep).length;
        setSelectionCount(countInThatStep % (draftSeq[lastStep]?.count || 1));
        setStepIndex(lastStep >= 0 ? lastStep : stepIndex);
      }
      return newH;
    });

    setAlertMsg('');
  }

  function startSeries(type) {
    setBoType(type);
    setCurrentGame(1);

    setGlobalPicks({ A: [], B: [] });
    setCompletedGames([]);
    setViewingHistory(null);

    setStepIndex(0);
    setSelectionCount(0);

    setBans({ A: [], B: [] });
    setPicks({ A: [], B: [] });
    setHistory([]);

    setAlertMsg('');
    setSelectedRole('ทั้งหมด');
    setSearchTerm('');

    setPreChoose({ A: [null, null, null, null, null], B: [null, null, null, null, null] });
    setPickRoles(createDefaultPickRoles());
    setSelectingPre(null);

    setDraftMode('sequence');
    setFreeAction('pick');
  }

  function nextGame() {
    if (currentGame < totalGames) {
      setCurrentGame((g) => g + 1);

      setStepIndex(0);
      setSelectionCount(0);

      setBans({ A: [], B: [] });
      setPicks({ A: [], B: [] });
      setHistory([]);

      setAlertMsg('');
      setSelectedRole('ทั้งหมด');
      setSearchTerm('');

      setPreChoose({ A: [null, null, null, null, null], B: [null, null, null, null, null] });
      setPickRoles(createDefaultPickRoles());
      setSelectingPre(null);

      setDraftMode('sequence');
    }
  }

  function makeSnapshot() {
    return {
      gameNo: currentGame,
      time: new Date().toISOString(),
      bans: JSON.parse(JSON.stringify(bans)),
      picks: JSON.parse(JSON.stringify(picks)),
      pickRoles: JSON.parse(JSON.stringify(pickRoles)),
      meta: {
        boType,
        ourTeamAtGame: ourTeam,
        weAreFirst,
      },
    };
  }

  function loadSnapshot(snap) {
    if (!snap) return;

    setDraftMode('free');
    setFreeAction('pick');

    setStepIndex(0);
    setSelectionCount(0);

    setBans(JSON.parse(JSON.stringify(snap.bans || { A: [], B: [] })));
    setPicks(JSON.parse(JSON.stringify(snap.picks || { A: [], B: [] })));
    setPickRoles(JSON.parse(JSON.stringify(snap.pickRoles || createDefaultPickRoles())));

    setHistory([]);
    setAlertMsg('');
    setSelectedRole('ทั้งหมด');
    setSearchTerm('');

    setPreChoose({ A: [null, null, null, null, null], B: [null, null, null, null, null] });
    setSelectingPre(null);
  }

  /* auto-save เมื่อ sequence จบเกม */
  useEffect(() => {
    if (draftMode === 'free') return;
    const finished = stepIndex >= totalSteps;
    if (!boType || !finished) return;

    const snap = makeSnapshot();
    setCompletedGames((prev) => {
      const idx = snap.gameNo - 1;
      const arr = prev.slice();
      if (arr[idx]) return arr;
      while (arr.length < idx) arr.push(null);
      arr[idx] = snap;
      return arr;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boType, stepIndex, totalSteps, currentGame, bans, picks, draftMode]);

  /* Esc ยกเลิก pre-choose select */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setSelectingPre(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
// เมื่อมีไฟล์ Excel เข้ามา ให้ Parse ใหม่ทันที
  useEffect(() => {
    if (!excelFileBlob) return;
    
    // Import ฟังก์ชันที่เราเพิ่งแก้ใน excelParser
    import('../utils/excelParser').then(async ({ parseScrimStatsFromExcelP, parseTeamCombosFromExcelP, parseRplBanPickFromExcelP, parseFsOneHeroFromRplPlayerStatsP }) => {
      try {
        console.log("Processing new Excel file...");
        
        // 1. อ่านประวัติซ้อม (Scrim)
        const parsedScrims = await parseScrimStatsFromExcelP(excelFileBlob);
        setScrimStats(parsedScrims?.scrimStats || {});

        // 1.1 อ่าน RPL_Ban_Pick เพื่อทำ meta stats
        const banPick = await parseRplBanPickFromExcelP(excelFileBlob);
        setBanPickTotalGames(banPick?.totalGames || 0);

        // 1.2 อ่าน one-hero ฝั่ง FS จาก RPL_player_stats (ให้ตรงกับ TierData)
        const fsOneHero = await parseFsOneHeroFromRplPlayerStatsP(excelFileBlob);
        setFsOneHeroFromRplStats(Array.isArray(fsOneHero) ? fsOneHero : []);

        // 2. อ่านข้อมูลคู่แข่ง (Team Intel)
        const parsedTeams = await parseTeamCombosFromExcelP(excelFileBlob);
        setAllTeamsIntel(parsedTeams.byTeam || {});
        
        // Auto-select opponent ทีมแรกที่เจอ (ถ้ายังไม่ได้เลือก)
        if (!opponent && parsedTeams.byTeam) {
            const firstTeam = Object.keys(parsedTeams.byTeam)[0];
            if (firstTeam) setOpponent(firstTeam);
        }
        
      } catch (err) {
        console.error("Parse Error:", err);
        setAlertMsg("อ่านไฟล์ Excel ไม่สำเร็จ");
      }
    });
  }, [excelFileBlob, opponent]); // รันใหม่เมื่อไฟล์เปลี่ยน

  // พูลจากชีทส่อง: พาร์สไฟล์ครั้งเดียวแล้วเก็บไว้ สลับทีมไม่ต้องอ่านใหม่
  useEffect(() => { specterRowsRef.current = null; }, [excelFileBlob]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!excelFileBlob || !opponent || !useScoutedPool) {
        setScoutedPool(EMPTY_OPP_POOL);
        return;
      }
      try {
        if (!specterRowsRef.current) {
          const { parseSpecterScrimP } = await import('../utils/excelParser');
          const log = await parseSpecterScrimP(excelFileBlob);
          specterRowsRef.current = log?.games || [];
        }
        const { scoutedOpponentPool } = await import('../utils/tactic/teamScout');
        const pool = scoutedOpponentPool(specterRowsRef.current, opponent);
        if (!cancelled) setScoutedPool(pool);
      } catch (err) {
        if (!cancelled) setScoutedPool(EMPTY_OPP_POOL);
      }
    })();
    return () => { cancelled = true; };
  }, [excelFileBlob, opponent, useScoutedPool]);

  // ใช้แหล่งข้อมูลเดียวกับ TierData สำหรับ Opponent Pool
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!excelFileBlob || !opponent) {
        setOpponentPoolTierData(EMPTY_OPP_POOL);
        return;
      }
      try {
        const { parseOpponentCombosForTeam, normalizeOppToComboStats } = await import('../utils/excelParser');
        const raw = await parseOpponentCombosForTeam(excelFileBlob, opponent);
        const norm = normalizeOppToComboStats(raw);
        if (!cancelled) setOpponentPoolTierData(norm || EMPTY_OPP_POOL);
      } catch (err) {
        if (!cancelled) setOpponentPoolTierData(EMPTY_OPP_POOL);
      }
    })();
    return () => { cancelled = true; };
  }, [excelFileBlob, opponent]);

  /* =========================
     Render helpers
     ========================= */
  function renderSlots(arr, max, team, type) {
    return Array.from({ length: max }).map((_, i) => {
      if (type === 'pick') {
        const hero = arr[i];
        const preHero = preChoose[team][i];
        const roleLabel = pickRoles[team]?.[i] || PICK_ROLE_DEFAULT;

        const slotCardStyle = {
          textAlign: 'center',
          width: 76,
          height: 100,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        };

        const isHighlight =
          draftMode === 'sequence' &&
          team === highlightTeam &&
          type === highlightType &&
          i >= highlightStartIndex &&
          i < highlightStartIndex + highlightCount;
        let slotContent = (
          <div
            style={{
              width: 76,
              height: 100,
              border: `2.5px dashed ${isHighlight ? highlightColor : '#444'}`,
              borderRadius: 14,
              background: '#21222b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              cursor: draftMode === 'sequence' && !markMode ? 'pointer' : 'default',
              color: '#888',
              fontWeight: 800,
              fontSize: 13,
              letterSpacing: 1,
            }}
            onClick={() => {
              if (draftMode === 'free' || markMode) return;
              setSelectingPre({ team, i });
            }}
            title={draftMode === 'free' || markMode ? 'Quick slot' : 'คลิกเพื่อเลือก Pre-choose'}
          >
            <span style={{ fontSize: 18 }}>＋</span>
            <span style={{ fontSize: 12, marginTop: 2 }}>
              {draftMode === 'free' || markMode ? 'Quick' : 'Pre-choose'}
            </span>
          </div>
        );

        if (hero) {
          slotContent = (
            <div style={slotCardStyle}>
              <img
                src={getImage(hero.image)}
                alt={hero.name}
                style={{ width: 72, height: 72, borderRadius: 14, boxShadow: '0 2px 12px #000a' }}
              />
              <div
                style={{
                  maxWidth: 72,
                  fontSize: 13,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: '#fff',
                  fontWeight: 700,
                }}
              >
                {hero.name}
              </div>
            </div>
          );
        } else if (preHero) {
          slotContent = (
            <div
              style={{
                ...slotCardStyle,
                background: '#23242e',
                border: '2.5px dashed #FFD700',
                borderRadius: 14,
                position: 'relative',
              }}
              title="Pre-choose"
            >
              <img
                src={getImage(preHero.image)}
                alt={preHero.name}
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: 14,
                  opacity: 0.54,
                  filter: 'grayscale(70%)',
                }}
              />
              <div
                style={{
                  maxWidth: 72,
                  fontSize: 13,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: '#FFD700',
                  fontWeight: 900,
                }}
              >
                {preHero.name} <span style={{ fontSize: 11 }}>[PRE]</span>
              </div>
              <button
                onClick={() => handleClearPreChoose(team, i)}
                title="ลบ pre-choose"
                style={{
                  position: 'absolute',
                  top: 3,
                  right: 5,
                  background: 'transparent',
                  border: 'none',
                  color: '#FFD700',
                  fontSize: 16,
                  cursor: 'pointer',
                }}
              >
                ×
              </button>
            </div>
          );
        }

        return (
          <div
            key={i}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              margin: '0 0 10px',
            }}
          >
            {slotContent}
            <select
              value={roleLabel}
              onChange={(e) => setPickRole(team, i, e.target.value)}
              title="เลือกตำแหน่ง"
              style={{
                minWidth: 62,
                height: 28,
                padding: '0 8px',
                borderRadius: 10,
                border: '1px solid rgba(87,234,231,.5)',
                background: 'rgba(10,18,24,.82)',
                color: '#57eae7',
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: 0.4,
                cursor: 'pointer',
                boxShadow: '0 0 12px rgba(87,234,231,.14)',
              }}
            >
              <option value={PICK_ROLE_DEFAULT}>Default</option>
              {PICK_ROLE_OPTIONS.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </div>
        );
      }

      // ban slots
      if (i < arr.length) {
        const hero = arr[i];
        return (
          <div
            key={i}
            style={{
              margin: 6,
              textAlign: 'center',
              width: 76,
              height: 100,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={getImage(hero.image)}
              alt={hero.name}
              style={{ width: 72, height: 72, borderRadius: 14, boxShadow: '0 2px 12px #000a' }}
            />
            <div
              style={{
                maxWidth: 72,
                fontSize: 13,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: '#fff',
                fontWeight: 700,
              }}
            >
              {hero.name}
            </div>
          </div>
        );
      }

      const isHighlight =
        draftMode === 'sequence' &&
        team === highlightTeam &&
        type === highlightType &&
        i >= highlightStartIndex &&
        i < highlightStartIndex + highlightCount;

      return (
        <div
          key={i}
          style={{
            width: 76,
            height: 100,
            margin: 6,
            border: `2.5px dashed ${isHighlight ? highlightColor : DRAFT_THEME.borderStrong}`,
            borderRadius: 14,
            background: DRAFT_THEME.surfaceSoft,
          }}
        />
      );
    });
  }

  /* ===== Global indicator helper ===== */
  function getGlobalPlayedStatus(heroName) {
    return {
      playedByUs: globalPicks[ourTeam]?.includes(heroName),
      playedByThem: globalPicks[enemyTeam]?.includes(heroName),
    };
  }

  function renderHeroGrid(viewTeam) {
    return (
      <div className="fullsense-panel" style={{ padding: 12, borderRadius: 14, background: DRAFT_THEME.surface }}>
        <div style={{ marginBottom: 10, color: DRAFT_THEME.textStrong, fontWeight: 900 }}>
          ฮีโร่ฝั่ง {teamLabel(viewTeam)}
          <span style={{ marginLeft: 10, fontSize: 11, opacity: 0.75, color: DRAFT_THEME.textMuted }}>
            (Shift+Click = เปิด Intel)
          </span>
        </div>

        {ROLE_ORDER.map((role) => {
          const list = heroesByRole[role] || [];
          if (!list.length) return null;

          return (
            <div key={`${viewTeam}-${role}`} style={{ marginBottom: 20 }}>
              <div style={{ marginBottom: 6, fontSize: 15, fontWeight: 700, color: DRAFT_THEME.textStrong }}>
                {ROLE_ICON[role]} {role}
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: heroRowGap }}>
                {list.map((hero) => {
                  const localUsed = localUsedNames.includes(hero.name);

                  const blockedSeq =
                    draftMode === 'sequence' &&
                    highlightType === 'pick' &&
                    globalPicks[highlightTeam]?.includes(hero.name) &&
                    !isFinalGame;

                  const flowbornBlocked =
                    (draftMode === 'sequence' && highlightType === 'pick' && isFlowbornBlockedForTeam(highlightTeam, hero.name, picks[highlightTeam]?.length ?? -1)) ||
                    (draftMode === 'free' && freeAction === 'pick' && isFlowbornBlockedForTeam(viewTeam, hero.name, picks[viewTeam]?.length ?? -1));

                  const disabled =
                    !markMode &&
                    !selectingPre &&
                    (localUsed || blockedSeq || flowbornBlocked || (stepIndex >= totalSteps && draftMode === 'sequence'));

                  const nowPickedBy = picks.A.some((h) => h.name === hero.name)
                    ? 'A'
                    : picks.B.some((h) => h.name === hero.name)
                      ? 'B'
                      : null;

                  const nowBannedBy = bans.A.some((h) => h.name === hero.name)
                    ? 'A'
                    : bans.B.some((h) => h.name === hero.name)
                      ? 'B'
                      : null;

                  const { playedByUs, playedByThem } = getGlobalPlayedStatus(hero.name);

                  const showGlobalPlayedBadge =
                    draftMode === 'sequence' &&
                    currentStep?.type === 'ban' &&
                    !nowPickedBy &&
                    !nowBannedBy &&
                    (playedByUs || playedByThem);

                  return (
                    <motion.div
                      key={`${viewTeam}-${hero.name}`}
                      whileHover={!disabled ? { scale: 1.08, boxShadow: '0 0 20px rgba(255,16,38,.22)' } : {}}
                      onMouseEnter={(e) => {
                        if (e.ctrlKey) {
                          setSynergyTooltip({
                            hero,
                            x: e.clientX,
                            y: e.clientY
                          });
                        }
                      }}
                      onMouseLeave={() => setSynergyTooltip(null)}
                      onClick={(e) => {
if (e.ctrlKey && !e.shiftKey) {
    setSynergyTooltip({ 
      hero, 
      x: e.clientX, 
      y: e.clientY 
    });
    return;
  }
                        // ✅ Shift + Click => เปิด Hero Intel (ไม่ยุ่งกับ draft)
                        if (e?.shiftKey) {
                          openHeroIntelPanel(hero, viewTeam);
                          return;
                        }
                        
                        // 1) Pre-choose selection flow
                        if (selectingPre) {
                          const { team, i } = selectingPre;
                          const preCheck = canPreChoose(team, i, hero.name);
                          if (!preCheck.ok) {
                            setAlertMsg(preCheck.msg);
                            return;
                          }
                          if (
                            draftMode === 'sequence' &&
                            currentStep?.type === 'pick' &&
                            globalPicks[currentStep?.team || 'A']?.includes(hero.name) &&
                            !isFinalGame
                          ) {
                            setAlertMsg(`กฎ Global: ไม่สามารถวาง "${hero.name}" เป็นแผนในเกมนี้ได้`);
                            return;
                          }
                          handlePreChoose(team, i, hero);
                          setSelectingPre(null);
                          setAlertMsg('');
                          return;
                        }

                        // 2) Mark mode
                        if (markMode) {
                          markPickOrBan(hero);
                          return;
                        }

                        // 3) Normal modes
                        if (disabled) return;

                        if (draftMode === 'free') {
                          doFreeAction(hero, viewTeam, freeAction);
                        } else {
                          handleHeroClick(hero);
                        }
                      }}
                      style={{
                        width: heroCardSize.width,
                        height: heroCardSize.height,
                        cursor: selectingPre
                          ? 'copy'
                          : markMode
                            ? 'crosshair'
                            : disabled
                              ? 'not-allowed'
                              : 'pointer',
                        opacity: disabled && !markMode && !nowPickedBy && !nowBannedBy ? 0.45 : 1,
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: heroCardSize.radius,
                        background: DRAFT_THEME.surfaceStrong,
                        boxShadow: DRAFT_THEME.shadow,
                        border: `2px solid ${DRAFT_THEME.border}`,
                        transition: 'box-shadow .2s,transform .15s,border .14s',
                        touchAction: 'manipulation',
                      }}
                    >
                      <img
                        src={getImage(hero.image)}
                        alt={hero.name}
                        draggable={false}
                        style={{
                          width: '100%',
                          height: heroCardSize.imageHeight,
                          objectFit: 'cover',
                          borderRadius: `${heroCardSize.radius}px ${heroCardSize.radius}px 0 0`,
                        }}
                      />
                      <div
                        style={{
                          width: '100%',
                          height: heroCardSize.labelHeight,
                          background: 'rgba(255,246,247,.96)',
                          fontSize: heroCardSize.fontSize,
                          borderRadius: `0 0 ${heroCardSize.radius}px ${heroCardSize.radius}px`,
                          textAlign: 'center',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          fontWeight: 800,
                          color: DRAFT_THEME.textStrong,
                          borderTop: `1px solid ${DRAFT_THEME.border}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {hero.name}
                      </div>

                      {/* ป้าย BAN/PICK ปัจจุบัน */}
                      {nowBannedBy && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 4,
                            left: 4,
                            padding: '2px 8px',
                            fontSize: 11,
                            fontWeight: 900,
                            color: '#fff',
                            background: ACTION_COLOR.ban,
                            borderRadius: 6,
                            letterSpacing: 0.5,
                          }}
                        >
                          {TEAM_TAG[nowBannedBy]} BAN
                        </div>
                      )}

                      {nowPickedBy && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            padding: '2px 8px',
                            fontSize: 11,
                            fontWeight: 900,
                            color: PICK_TEXT_COLOR,
                            background: ACTION_COLOR.pick,
                            borderRadius: 6,
                            letterSpacing: 0.5,
                          }}
                        >
                          {TEAM_TAG[nowPickedBy]} PICK
                        </div>
                      )}

                      {/* แสดงว่าเรา/เค้าเล่นไปแล้ว (Global) ตอนช่วง Ban */}
                      {showGlobalPlayedBadge && playedByUs && (
                        <div
                          title="เราเคยเล่นฮีโร่นี้ในเกมก่อนหน้า"
                          style={{
                            position: 'absolute',
                            bottom: 4,
                            left: 4,
                            padding: '2px 8px',
                            fontSize: 10,
                            fontWeight: 900,
                            color: PICK_TEXT_COLOR,
                            background: ACTION_COLOR.pick,
                            borderRadius: 6,
                            letterSpacing: 0.3,
                            boxShadow: '0 2px 8px #0008',
                          }}
                        >
                          เราเล่นแล้ว
                        </div>
                      )}

                      {showGlobalPlayedBadge && playedByThem && (
                        <div
                          title="ฝั่งตรงข้ามเคยเล่นฮีโร่นี้ในเกมก่อนหน้า"
                          style={{
                            position: 'absolute',
                            bottom: 4,
                            right: 4,
                            padding: '2px 8px',
                            fontSize: 10,
                            fontWeight: 900,
                            color: '#fff',
                            background: '#ff4d9a',
                            borderRadius: 6,
                            letterSpacing: 0.3,
                            boxShadow: '0 2px 8px #0008',
                          }}
                        >
                          เค้าเล่นแล้ว
                        </div>
                      )}

                      {/* Overlay เมื่อถูกใช้ในเกมนี้แล้ว */}
                      {(nowPickedBy || nowBannedBy) && (
                        <UsedOverlay mode={nowBannedBy ? 'ban' : 'pick'} />
                      )}

                      {/* Mark mode frame */}
                      {markMode && !nowPickedBy && !nowBannedBy && (
                        <div
                          style={{
                            position: 'absolute',
                            inset: 0,
                            pointerEvents: 'none',
                            boxShadow: markTool.startsWith('ban')
                              ? 'inset 0 0 0 2px #ea1c24, inset 0 0 12px #ea1c2488'
                              : 'inset 0 0 0 2px #57eae7, inset 0 0 12px #57eae788',
                            borderRadius: 16,
                          }}
                        />
                      )}
                      {/* ===== INLINE SYNERGY BADGE ===== */}
{!nowPickedBy && !nowBannedBy && (
  <InlineSynergyBadge
    hero={hero}
    teamPicks={picks[viewTeam]}
    synergyData={null}
  />
)}
                      {/* Pre-choose frame */}
                      {selectingPre && (
                        <div
                          style={{
                            position: 'absolute',
                            inset: 0,
                            pointerEvents: 'none',
                            boxShadow: 'inset 0 0 0 2px #FFD700, inset 0 0 10px #FFD70066',
                            borderRadius: 16,
                          }}
                        />
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  /* =========================
     BO Selector Screen
     ========================= */
  if (!boType) {
    return (
      <div className="fullsense-root">
        <div className="fullsense-grid" />
        <div className="fullsense-stripe" />
        <div className="fullsense-content"
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 24,
          }}
        >
          <h1 style={{ color: DRAFT_THEME.textStrong, marginBottom: 32, letterSpacing: 2, fontWeight: 800 }}>
            เลือกรูปแบบ BO
          </h1>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
            {Object.keys(BO_OPTIONS).map((t) => (
              <button
                key={t}
                onClick={() => startSeries(t)}
                style={{
                  padding: '18px 44px',
                  fontSize: 26,
                  fontWeight: 800,
                  borderRadius: 18,
                  background: 'var(--orange)',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  boxShadow: '0 0 26px var(--orange-glow)',
                }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const panelColors = {
    A: weAreFirst ? 'rgba(255,16,38,.16)' : 'rgba(255,255,255,.92)',
    B: weAreFirst ? 'rgba(255,255,255,.92)' : 'rgba(255,16,38,.16)',
  };

  /* =========================
     History Mode
     ========================= */
  if (viewingHistory !== null) {
    return (
      <HistoryView
        list={completedGames.filter(Boolean)}
        onBack={() => setViewingHistory(null)}
        onLoad={(snap) => {
          loadSnapshot(snap);
          setViewingHistory(null);
        }}
      />
    );
  }

  /* =========================
     Main Render
     ========================= */
  return (
    <div className="fullsense-root">
      <div className="fullsense-grid" />
      <div className="fullsense-stripe" />

      <div className="fullsense-content">
        {/* Top tool strip */}
        <div
          className="fullsense-panel"
          style={{
            width: '100%',
            padding: '12px 24px',
            background: DRAFT_THEME.surfaceStrong,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 14,
            flexWrap: 'wrap',
            borderRadius: 14,
            margin: 12,
          }}
        >
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <strong style={{ color: DRAFT_THEME.textStrong }}>โหมดดราฟ:</strong>
            <Pill active={draftMode === 'sequence'} onClick={() => setDraftMode('sequence')}>
              Sequence
            </Pill>
            <Pill active={draftMode === 'free'} onClick={() => setDraftMode('free')}>
              Free
            </Pill>
          </div>

          {draftMode === 'free' && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <strong style={{ color: DRAFT_THEME.textStrong }}>Free action:</strong>
              <Pill active={freeAction === 'pick'} onClick={() => setFreeAction('pick')}>
                Pick
              </Pill>
              <Pill active={freeAction === 'ban'} onClick={() => setFreeAction('ban')}>
                Ban
              </Pill>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <strong style={{ color: DRAFT_THEME.textStrong }}>เราฝั่ง:</strong>
            <label style={{ color: DRAFT_THEME.text }}>
              <input type="radio" checked={weAreFirst} onChange={() => setWeAreFirst(true)} /> First
            </label>
            <label style={{ color: DRAFT_THEME.text }}>
              <input type="radio" checked={!weAreFirst} onChange={() => setWeAreFirst(false)} /> Second
            </label>
          </div>

          <div>
            <strong style={{ color: DRAFT_THEME.textStrong, marginRight: 8 }}>แสดงแบน</strong>
            <label style={{ marginRight: 12, color: DRAFT_THEME.text }}>
              <input
                type="radio"
                checked={banLayout === 'vertical'}
                onChange={() => setBanLayout('vertical')}
              />{' '}
              แนวตั้ง
            </label>
            <label style={{ color: DRAFT_THEME.text }}>
              <input
                type="radio"
                checked={banLayout === 'horizontal'}
                onChange={() => setBanLayout('horizontal')}
              />{' '}
              แนวนอน
            </label>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => {
                const snap = makeSnapshot();
                setCompletedGames((prev) => {
                  const idx = snap.gameNo - 1;
                  const arr = prev.slice();
                  while (arr.length < idx) arr.push(null);
                  arr[idx] = snap;
                  return arr;
                });
                setAlertMsg('Saved snapshot!');
                setTimeout(() => setAlertMsg(''), 1200);
              }}
              style={{ ...btnGhost, background: 'var(--orange)', color: '#fff', boxShadow: '0 0 18px var(--orange-glow)' }}
            >
              💾 Save Game
            </button>

            <button
              onClick={() => setViewingHistory({ view: 'list' })}
              style={{ ...btnGhost, background: DRAFT_THEME.surface }}
            >
              🕘 History
            </button>

            <button
              onClick={handleUndo}
              disabled={!history.length}
              style={{
                ...btnGhost,
                opacity: history.length ? 1 : 0.5,
                cursor: history.length ? 'pointer' : 'not-allowed',
              }}
            >
              ↩ Undo
            </button>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={() => setMarkMode((v) => !v)}
              style={{
                padding: '8px 12px',
                borderRadius: 10,
                border: 'none',
                background: markMode ? 'var(--orange)' : DRAFT_THEME.surface,
                color: markMode ? '#fff' : DRAFT_THEME.textStrong,
                fontWeight: 900,
                cursor: 'pointer',
                boxShadow: markMode ? '0 0 16px var(--orange-glow)' : 'none',
              }}
            >
              {markMode ? 'Mark Mode: ON' : 'Mark Mode: OFF'}
            </button>

            {markMode && (
              <>
                <button
                  onClick={() => setMarkTool('pickA')}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 10,
                    border: 'none',
                    background: markTool === 'pickA' ? ACTION_COLOR.pick : DRAFT_THEME.surface,
                    color: PICK_TEXT_COLOR,
                    fontWeight: 900,
                  }}
                >
                  Pick A
                </button>
                <button
                  onClick={() => setMarkTool('pickB')}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 10,
                    border: 'none',
                    background: markTool === 'pickB' ? ACTION_COLOR.pick : DRAFT_THEME.surface,
                    color: PICK_TEXT_COLOR,
                    fontWeight: 900,
                  }}
                >
                  Pick B
                </button>
                <button
                  onClick={() => setMarkTool('banA')}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 10,
                    border: 'none',
                    background: markTool === 'banA' ? ACTION_COLOR.ban : DRAFT_THEME.surface,
                    color: '#fff',
                    fontWeight: 900,
                  }}
                >
                  Ban A
                </button>
                <button
                  onClick={() => setMarkTool('banB')}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 10,
                    border: 'none',
                    background: markTool === 'banB' ? ACTION_COLOR.ban : DRAFT_THEME.surface,
                    color: '#fff',
                    fontWeight: 900,
                  }}
                >
                  Ban B
                </button>
              </>
            )}
          </div>

          {selectingPre && (
            <span style={{ color: '#FFD700', fontWeight: 900, fontSize: 12 }}>
              กำลังเลือก PRE — Team {selectingPre.team} Slot {selectingPre.i + 1} (กด Esc เพื่อยกเลิก)
            </span>
          )}
        </div>
        {/* Main body */}
        <div style={{ display: 'flex', height: 'calc(100vh - 96px)', position: 'relative', padding: 12 }}>
          {/* LEFT PANEL */}
          <div
            className="fullsense-panel"
            style={{
              width: sidePanelWidth,
              background: panelColors[LEFT_TEAM_ID],
              padding: isTabletViewport ? Math.max(8, sidePanelPadding - 1) : sidePanelPadding,
              boxShadow: DRAFT_THEME.shadow,
              borderRadius: '0 28px 28px 0',
              border: `2px solid ${highlightTeam === LEFT_TEAM_ID ? highlightColor : DRAFT_THEME.border}`,
            }}
          >
            <h4 style={{ color: DRAFT_THEME.textStrong, textAlign: 'center' }}>
              {teamLabel(LEFT_TEAM_ID)}
              {weAreFirst && (
                <span
                  style={{
                    marginLeft: 6,
                    padding: '2px 6px',
                    borderRadius: 8,
                    background: 'var(--orange)',
                    color: '#fff',
                    fontWeight: 900,
                    fontSize: 11,
                    boxShadow: '0 0 12px var(--orange-glow)',
                  }}
                >
                  FS
                </span>
              )}
            </h4>

            {banLayout === 'vertical' && (
              <>
                <strong style={{ color: DRAFT_THEME.textStrong }}>Bans</strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
                  {renderSlots(bans[LEFT_TEAM_ID], 4, LEFT_TEAM_ID, 'ban')}
                </div>
              </>
            )}

            <strong style={{ color: PICK_TEXT_COLOR }}>Picks</strong>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
              {renderSlots(picks[LEFT_TEAM_ID], 5, LEFT_TEAM_ID, 'pick')}
            </div>
          </div>

          {/* CENTER */}
          <div
            className="fullsense-panel"
            style={{
              flex: 1,
              position: 'relative',
              overflowY: 'auto',
              padding: isCompactDraftLayout ? Math.max(10, centerPanelPadding - 2) : centerPanelPadding,
              margin: `0 ${centerPanelMarginX}px`,
              background: DRAFT_THEME.surface,
              borderRadius: 18,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
              {false && (
                <>
              {/* Draft Copilot */}
              <div
                className="fullsense-panel"
                style={{
                  margin: '0 0 14px',
                  background: 'linear-gradient(145deg, rgba(10,11,15,.78), rgba(20,20,26,.72))',
                  borderRadius: 14,
                  padding: 14,
                  border: '1px solid rgba(255,255,255,.10)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    flexWrap: 'wrap',
                    marginBottom: 10,
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ fontWeight: 900, color: '#fff', fontSize: 16, letterSpacing: 0.4 }}>
                      Draft Copilot
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <span
                        style={{
                          background: 'rgba(87,234,231,.18)',
                          color: '#57eae7',
                          fontSize: 11,
                          fontWeight: 900,
                          padding: '3px 8px',
                          borderRadius: 999,
                          border: '1px solid rgba(87,234,231,.28)',
                        }}
                      >
                        OUR PICKS
                      </span>
                      <span
                        style={{
                          background: isEnemyTurn ? 'rgba(255,16,38,.20)' : 'rgba(255,255,255,.08)',
                          color: isEnemyTurn ? 'var(--orange)' : '#c2c8de',
                          fontSize: 11,
                          fontWeight: 900,
                          padding: '3px 8px',
                          borderRadius: 999,
                          border: '1px solid rgba(255,255,255,.16)',
                        }}
                      >
                        {isEnemyTurn ? 'ENEMY TURN' : 'WAIT ENEMY TURN'}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ color: '#cfd6ff', fontWeight: 800, fontSize: 12 }}>Opponent</div>
                    <select
                      value={opponent}
                      onChange={(e) => setOpponent(e.target.value)}
                      style={{
                        background: 'rgba(0,0,0,.25)',
                        color: '#fff',
                        border: '1px solid rgba(255,255,255,.20)',
                        borderRadius: 10,
                        padding: '7px 12px',
                        fontWeight: 800,
                      }}
                    >
                      {(intel?.teams || []).map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setUseScoutedPool((v) => !v)}
                      title="ชีท Specter_Scrim — รวมเกมของทีมนี้ที่เราส่องมา เข้าไปใน Opponent Pool"
                      style={{
                        background: useScoutedPool ? 'rgba(255,16,38,.22)' : 'rgba(0,0,0,.25)',
                        color: useScoutedPool ? '#ffb182' : '#cfd6ff',
                        border: `1px solid ${useScoutedPool ? 'rgba(255,16,38,.55)' : 'rgba(255,255,255,.20)'}`,
                        borderRadius: 10,
                        padding: '7px 12px',
                        fontWeight: 800,
                        fontSize: 12,
                        cursor: 'pointer',
                      }}
                    >
                      {useScoutedPool ? '✓ ' : '+ '}ที่ส่องมา
                      {useScoutedPool && scoutedPool?.games ? ` (${scoutedPool.games})` : ''}
                    </button>
                    <span style={{ color: '#9aa3c7', fontSize: 12, fontWeight: 700 }}>
                      {coreStatGames?.length ? `games: ${coreStatGames.length}` : 'no games loaded'}
                    </span>
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      background: 'rgba(87,234,231,.06)',
                      borderRadius: 12,
                      padding: 10,
                      border: '1px solid rgba(87,234,231,.22)',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <div style={{ fontWeight: 900, color: '#57eae7' }}>Our Best Picks</div>
                      <div style={{ color: '#9aa3c7', fontSize: 11, fontWeight: 800 }}>
                        Top {bestForUs.length || 0}
                      </div>
                    </div>
                    {bestForUs.length === 0 ? (
                      <div style={{ color: '#9aa3c7', fontSize: 12 }}>ยังไม่มีข้อมูลแนะนำ (เช็คว่าโหลด Excel แล้ว)</div>
                    ) : (
                      <div
                        style={{
                          display: 'grid',
                          gridAutoFlow: 'column',
                          gridTemplateRows: 'repeat(2, minmax(0, 1fr))',
                          gridAutoColumns: 'minmax(240px, 1fr)',
                          gap: 8,
                          overflowX: 'auto',
                          overflowY: 'hidden',
                          paddingBottom: 6,
                        }}
                      >
                        {bestForUs.map((x, idx) => {
                          const heroData = heroByName[x.hero];
                          return (
                          <div
                            key={x.hero}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '34px 1fr auto',
                              alignItems: 'center',
                              gap: 10,
                              padding: '8px 10px',
                              borderRadius: 10,
                              background: 'rgba(0,0,0,.24)',
                              border: '1px solid rgba(255,255,255,.10)',
                            }}
                          >
                            <div
                              style={{
                                width: 34,
                                height: 34,
                                borderRadius: 8,
                                overflow: 'hidden',
                                border: '1px solid rgba(255,255,255,.18)',
                                background: 'rgba(255,255,255,.05)',
                              }}
                            >
                              {heroData?.image && (
                                <img
                                  src={getImage(heroData.image)}
                                  alt={x.hero}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              )}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div
                                style={{
                                  color: '#fff',
                                  fontWeight: 900,
                                  fontSize: 13,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 7,
                                }}
                              >
                                <span style={{ color: '#57eae7' }}>#{idx + 1}</span>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {x.hero}
                                </span>
                              </div>
                              <div style={{ marginTop: 3, fontSize: 11, color: '#cfd6ff', lineHeight: 1.3 }}>
                                {(x.explain || []).slice(0, 1).map((r, i) => (<div key={i}>• {r}</div>))}
                              </div>
                            </div>
                            <div
                              style={{
                                color: '#0f1220',
                                fontWeight: 900,
                                fontSize: 12,
                                background: '#57eae7',
                                borderRadius: 999,
                                padding: '4px 8px',
                              }}
                            >
                              {Math.round(x.score)}
                            </div>
                          </div>
                        );
                        })}
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      background: 'rgba(255,16,38,.06)',
                      borderRadius: 12,
                      padding: 10,
                      border: '1px solid rgba(255,16,38,.28)',
                    }}
                  >
                    <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ fontWeight: 900, color: 'var(--orange)' }}>
                        Opponent Next Move
                      </div>
                      <div style={{ color: '#9aa3c7', fontSize: 11, fontWeight: 800 }}>Top {enemyPredictionList.length || 0}</div>
                    </div>
                    <div style={{ fontSize: 11, color: '#9aa3c7', marginBottom: 8 }}>
                      Based on Opponent Pool (Score calibrated to Our Picks scale)
                    </div>
                    {!isEnemyTurn && (
                      <div style={{ fontSize: 12, color: '#9aa3c7', marginBottom: 8 }}>
                        (รอจังหวะฝั่งตรงข้าม)
                      </div>
                    )}
                    {enemyPredictionList.length === 0 ? (
                      <div style={{ color: '#9aa3c7', fontSize: 12 }}>ยังไม่มี prediction ในจังหวะนี้</div>
                    ) : (
                      <div
                        style={{
                          display: 'grid',
                          gridAutoFlow: 'column',
                          gridTemplateRows: 'repeat(2, minmax(0, 1fr))',
                          gridAutoColumns: 'minmax(240px, 1fr)',
                          gap: 8,
                          overflowX: 'auto',
                          overflowY: 'hidden',
                          paddingBottom: 6,
                        }}
                      >
                        {enemyPredictionList.map((p, idx) => {
                          const heroData = heroByName[p.hero];
                          return (
                          <div
                            key={p.hero}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '34px 1fr auto',
                              alignItems: 'center',
                              gap: 10,
                              padding: '8px 10px',
                              borderRadius: 10,
                              background: 'rgba(0,0,0,.24)',
                              border: '1px solid rgba(255,255,255,.10)',
                            }}
                          >
                            <div
                              style={{
                                width: 34,
                                height: 34,
                                borderRadius: 8,
                                overflow: 'hidden',
                                border: '1px solid rgba(255,255,255,.18)',
                                background: 'rgba(255,255,255,.05)',
                              }}
                            >
                              {heroData?.image && (
                                <img
                                  src={getImage(heroData.image)}
                                  alt={p.hero}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              )}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div
                                style={{
                                  color: '#fff',
                                  fontWeight: 900,
                                  fontSize: 13,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 7,
                                }}
                              >
                                <span style={{ color: 'var(--orange)' }}>#{idx + 1}</span>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {p.hero}
                                </span>
                              </div>
                              <div style={{ marginTop: 3, fontSize: 11, color: '#cfd6ff', lineHeight: 1.3 }}>
                                {(p.reason || []).slice(0, 2).map((r, i) => (<div key={i}>• {r}</div>))}
                              </div>
                            </div>
                            <div
                              style={{
                                color: '#101016',
                                fontWeight: 900,
                                fontSize: 12,
                                background: 'var(--orange)',
                                borderRadius: 999,
                                padding: '4px 8px',
                              }}
                            >
                              {Math.round(Number(p.score || 0))}
                            </div>
                          </div>
                        );
                        })}
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      position: 'fixed',
                      top: 72,
                      right: 10,
                      bottom: 14,
                      width: 'min(860px, calc(100vw - 22px))',
                      background: 'linear-gradient(180deg, rgba(255,16,38,.12), rgba(255,16,38,.04))',
                      borderRadius: 14,
                      padding: 12,
                      border: '1px solid rgba(255,16,38,.28)',
                      boxShadow: '0 14px 44px rgba(0,0,0,.42)',
                      zIndex: 9400,
                      transform: isOpponentPoolOpen ? 'translateX(0)' : 'translateX(calc(100% + 18px))',
                      opacity: isOpponentPoolOpen ? 1 : 0,
                      pointerEvents: isOpponentPoolOpen ? 'auto' : 'none',
                      transition: 'transform .22s ease, opacity .18s ease',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ fontWeight: 900, color: '#ffd4b8' }}>
                          Opponent Hero Pool ({opponent || '-'})
                        </div>
                        <button
                          onClick={() => setOpponentPoolView('selected')}
                          style={{
                            border: '1px solid rgba(255,255,255,.14)',
                            background: opponentPoolView === 'selected' ? 'rgba(255,16,38,.30)' : 'rgba(0,0,0,.22)',
                            color: '#fff',
                            borderRadius: 999,
                            fontWeight: 800,
                            fontSize: 11,
                            padding: '4px 10px',
                            cursor: 'pointer',
                          }}
                        >
                          ทีมที่เลือก
                        </button>
                        <button
                          onClick={() => setOpponentPoolView('allTeams')}
                          style={{
                            border: '1px solid rgba(255,255,255,.14)',
                            background: opponentPoolView === 'allTeams' ? 'rgba(255,16,38,.30)' : 'rgba(0,0,0,.22)',
                            color: '#fff',
                            borderRadius: 999,
                            fontWeight: 800,
                            fontSize: 11,
                            padding: '4px 10px',
                            cursor: 'pointer',
                          }}
                        >
                          ทุกทีม
                        </button>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input
                            type="text"
                            value={poolSearchInput}
                            onChange={(e) => setPoolSearchInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') setPoolSearchKeyword(String(poolSearchInput || '').trim().toLowerCase());
                            }}
                            placeholder="ค้นหา Hero/Combo"
                            style={{
                              width: 150,
                              border: '1px solid rgba(255,255,255,.16)',
                              background: 'rgba(0,0,0,.22)',
                              color: '#fff',
                              borderRadius: 999,
                              fontSize: 11,
                              padding: '5px 10px',
                              outline: 'none',
                            }}
                          />
                          <button
                            onClick={() => setPoolSearchKeyword(String(poolSearchInput || '').trim().toLowerCase())}
                            style={{
                              border: '1px solid rgba(255,255,255,.14)',
                              background: 'rgba(255,16,38,.30)',
                              color: '#fff',
                              borderRadius: 999,
                              fontWeight: 800,
                              fontSize: 11,
                              padding: '4px 10px',
                              cursor: 'pointer',
                            }}
                          >
                            ค้นหา
                          </button>
                          {poolSearchKeyword && (
                            <button
                              onClick={() => {
                                setPoolSearchInput('');
                                setPoolSearchKeyword('');
                              }}
                              style={{
                                border: '1px solid rgba(255,255,255,.14)',
                                background: 'rgba(0,0,0,.20)',
                                color: '#fff',
                                borderRadius: 999,
                                fontWeight: 800,
                                fontSize: 11,
                                padding: '4px 9px',
                                cursor: 'pointer',
                              }}
                            >
                              ล้าง
                            </button>
                          )}
                        </div>
                        {OPP_POOL_POSITIONS.map((pos) => (
                          <button
                            key={pos}
                            onClick={() => setOpponentPoolPos(pos)}
                            style={{
                              border: '1px solid rgba(255,255,255,.14)',
                              background: opponentPoolPos === pos ? 'rgba(255,16,38,.30)' : 'rgba(0,0,0,.20)',
                              color: '#fff',
                              borderRadius: 999,
                              fontWeight: 800,
                              fontSize: 11,
                              padding: '4px 10px',
                              cursor: 'pointer',
                            }}
                          >
                            {pos}
                          </button>
                        ))}
                        <button
                          onClick={() => setIsOpponentPoolOpen(false)}
                          style={{
                            border: '1px solid rgba(255,255,255,.14)',
                            background: 'rgba(0,0,0,.30)',
                            color: '#fff',
                            borderRadius: 999,
                            fontWeight: 900,
                            fontSize: 11,
                            padding: '4px 10px',
                            cursor: 'pointer',
                          }}
                        >
                          ปิด
                        </button>
                      </div>
                    </div>

                    <div style={{ color: '#d7b8a3', fontSize: 11, marginBottom: 8 }}>
                      ตัวที่ถูก Pick/Ban แล้วจะถูกตัดออกจากพูลและคอมโบทันที
                    </div>

                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr',
                        gap: 10,
                        alignItems: 'start',
                        minHeight: 0,
                        overflowY: 'auto',
                        paddingRight: 2,
                      }}
                    >
                    <div style={{ minWidth: 0 }}>
                    {opponentPoolView === 'selected' ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
                      <div style={{ background: 'linear-gradient(180deg, rgba(40,8,14,.78), rgba(9,7,12,.92))', borderRadius: 10, padding: 8, border: '1px solid rgba(255,16,38,.24)' }}>
                        <div style={{ color: '#dbe4ff', fontWeight: 900, fontSize: 12, marginBottom: 6 }}>One Hero</div>
                        <div style={{ maxHeight: 230, overflow: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                              <tr style={{ color: '#9aa3c7', textAlign: 'left' }}>
                                <th style={{ ...stickyThStyle, padding: '5px 6px' }} />
                                <th style={{ ...stickyThStyle, padding: '5px 6px' }}>Hero</th>
                                <th style={{ ...stickyThStyle, padding: '5px 6px' }}>Pos</th>
                                <th style={{ ...stickyThStyle, padding: '5px 6px', cursor: 'pointer' }} onClick={() => onPoolSort('one', 'total')}>Pick{sortMark('one', 'total')}</th>
                                <th style={{ ...stickyThStyle, padding: '5px 6px', cursor: 'pointer' }} onClick={() => onPoolSort('one', 'winRate')}>WR{sortMark('one', 'winRate')}</th>
                                <th style={{ ...stickyThStyle, padding: '5px 6px', cursor: 'pointer' }} onClick={() => onPoolSort('one', 'pickRate')}>PR{sortMark('one', 'pickRate')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {opponentOneHeroRows.length === 0 && (
                                <tr>
                                  <td colSpan={6} style={{ padding: 8, color: '#9aa3c7' }}>ไม่มีข้อมูลตามเงื่อนไข</td>
                                </tr>
                              )}
                              {opponentOneHeroRows.map((r) => (
                                <tr key={`${r.hero}-${r.position}`} style={{ borderTop: '1px solid rgba(255,255,255,.08)' }}>
                                  <td style={{ padding: '6px 3px', width: 26 }}>{renderHeroAvatar(r.hero, 18)}</td>
                                  <td style={{ padding: '6px' }}>{r.hero}</td>
                                  <td style={{ padding: '6px', color: '#ffb07a' }}>{r.position}</td>
                                  <td style={{ padding: '6px' }}>{r.total}</td>
                                  <td style={{ padding: '6px' }}>{r.winRate.toFixed(1)}%</td>
                                  <td style={{ padding: '6px' }}>{r.pickRate.toFixed(2)}%</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div style={{ background: 'linear-gradient(180deg, rgba(40,8,14,.78), rgba(9,7,12,.92))', borderRadius: 10, padding: 8, border: '1px solid rgba(255,16,38,.24)' }}>
                        <div style={{ color: '#dbe4ff', fontWeight: 900, fontSize: 12, marginBottom: 6 }}>Combo 2</div>
                        <div style={{ maxHeight: 230, overflow: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                              <tr style={{ color: '#9aa3c7', textAlign: 'left' }}>
                                <th style={{ ...stickyThStyle, padding: '5px 6px' }}>Combo</th>
                                <th style={{ ...stickyThStyle, padding: '5px 6px', cursor: 'pointer' }} onClick={() => onPoolSort('combo', 'total')}>Pick{sortMark('combo', 'total')}</th>
                                <th style={{ ...stickyThStyle, padding: '5px 6px', cursor: 'pointer' }} onClick={() => onPoolSort('combo', 'winRate')}>WR{sortMark('combo', 'winRate')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {opponentCombo2Rows.length === 0 && (
                                <tr>
                                  <td colSpan={3} style={{ padding: 8, color: '#9aa3c7' }}>ไม่มีข้อมูลตามเงื่อนไข</td>
                                </tr>
                              )}
                              {opponentCombo2Rows.map((r) => (
                                <tr key={r.combo} style={{ borderTop: '1px solid rgba(255,255,255,.08)' }}>
                                  <td style={{ padding: '6px' }}>{renderComboCell(r.combo)}</td>
                                  <td style={{ padding: '6px' }}>{r.total}</td>
                                  <td style={{ padding: '6px' }}>{r.winRate.toFixed(1)}%</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div style={{ background: 'linear-gradient(180deg, rgba(40,8,14,.78), rgba(9,7,12,.92))', borderRadius: 10, padding: 8, border: '1px solid rgba(255,16,38,.24)' }}>
                        <div style={{ color: '#dbe4ff', fontWeight: 900, fontSize: 12, marginBottom: 6 }}>Combo 3</div>
                        <div style={{ maxHeight: 230, overflow: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                              <tr style={{ color: '#9aa3c7', textAlign: 'left' }}>
                                <th style={{ ...stickyThStyle, padding: '5px 6px' }}>Combo</th>
                                <th style={{ ...stickyThStyle, padding: '5px 6px', cursor: 'pointer' }} onClick={() => onPoolSort('combo', 'total')}>Pick{sortMark('combo', 'total')}</th>
                                <th style={{ ...stickyThStyle, padding: '5px 6px', cursor: 'pointer' }} onClick={() => onPoolSort('combo', 'winRate')}>WR{sortMark('combo', 'winRate')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {opponentCombo3Rows.length === 0 && (
                                <tr>
                                  <td colSpan={3} style={{ padding: 8, color: '#9aa3c7' }}>ไม่มีข้อมูลตามเงื่อนไข</td>
                                </tr>
                              )}
                              {opponentCombo3Rows.map((r) => (
                                <tr key={r.combo} style={{ borderTop: '1px solid rgba(255,255,255,.08)' }}>
                                  <td style={{ padding: '6px' }}>{renderComboCell(r.combo)}</td>
                                  <td style={{ padding: '6px' }}>{r.total}</td>
                                  <td style={{ padding: '6px' }}>{r.winRate.toFixed(1)}%</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                    ) : (
                    <div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(460px, 1fr))', gap: 10 }}>
                      {opponentPoolByTeams.length === 0 && (
                        <div style={{ color: '#9aa3c7', fontSize: 12 }}>ยังไม่มีข้อมูลทีมฝั่งตรงข้าม</div>
                      )}
                      {visibleOpponentPoolByTeams.map((pkg) => (
                        <div
                          key={pkg.team}
                          style={{
                            background: 'rgba(0,0,0,.22)',
                            borderRadius: 10,
                            padding: 8,
                            border: `1px solid ${opponent === pkg.team ? 'rgba(255,16,38,.50)' : 'rgba(255,255,255,.08)'}`,
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <div style={{ color: '#dbe4ff', fontWeight: 900, fontSize: 13 }}>{pkg.team}</div>
                            <button
                              onClick={() => {
                                setOpponent(pkg.team);
                                setOpponentPoolView('selected');
                              }}
                              style={{
                                border: '1px solid rgba(255,255,255,.14)',
                                background: 'rgba(255,16,38,.22)',
                                color: '#fff',
                                borderRadius: 999,
                                fontWeight: 800,
                                fontSize: 10,
                                padding: '3px 8px',
                                cursor: 'pointer',
                              }}
                            >
                              เลือกทีมนี้
                            </button>
                          </div>
                          <div style={{ color: '#9aa3c7', fontSize: 11, marginBottom: 8 }}>
                            One Hero: {pkg.oneHeroRows.length} | Combo2: {pkg.combo2Rows.length} | Combo3: {pkg.combo3Rows.length}
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: 6 }}>
                            <div style={{ background: 'linear-gradient(180deg, rgba(32,7,12,.8), rgba(7,6,11,.94))', borderRadius: 8, padding: 6, border: '1px solid rgba(255,16,38,.18)' }}>
                              <div style={{ color: '#dbe4ff', fontWeight: 900, fontSize: 11, marginBottom: 4 }}>One Hero</div>
                              <div style={{ maxHeight: 170, overflow: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                  <thead>
                                    <tr style={{ color: '#9aa3c7', textAlign: 'left' }}>
                                      <th style={{ ...stickyThStyle, padding: '4px 5px' }} />
                                      <th style={{ ...stickyThStyle, padding: '4px 5px' }}>Hero</th>
                                      <th style={{ ...stickyThStyle, padding: '4px 5px' }}>Pos</th>
                                      <th style={{ ...stickyThStyle, padding: '4px 5px', cursor: 'pointer' }} onClick={() => onPoolSort('one', 'total')}>Pick{sortMark('one', 'total')}</th>
                                      <th style={{ ...stickyThStyle, padding: '4px 5px', cursor: 'pointer' }} onClick={() => onPoolSort('one', 'winRate')}>WR{sortMark('one', 'winRate')}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {pkg.oneHeroRows.length === 0 && (
                                      <tr>
                                        <td colSpan={5} style={{ padding: 6, color: '#9aa3c7' }}>ไม่มีข้อมูล</td>
                                      </tr>
                                    )}
                                    {pkg.oneHeroRows.slice(0, 8).map((r) => (
                                      <tr key={`${pkg.team}-${r.hero}-${r.position}`} style={{ borderTop: '1px solid rgba(255,255,255,.08)' }}>
                                        <td style={{ padding: '5px 3px', width: 24 }}>{renderHeroAvatar(r.hero, 16)}</td>
                                        <td style={{ padding: '5px' }}>{r.hero}</td>
                                        <td style={{ padding: '5px', color: '#ffb07a' }}>{r.position}</td>
                                        <td style={{ padding: '5px' }}>{r.total}</td>
                                        <td style={{ padding: '5px' }}>{r.winRate.toFixed(1)}%</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>

                            <div style={{ background: 'linear-gradient(180deg, rgba(32,7,12,.8), rgba(7,6,11,.94))', borderRadius: 8, padding: 6, border: '1px solid rgba(255,16,38,.18)' }}>
                              <div style={{ color: '#dbe4ff', fontWeight: 900, fontSize: 11, marginBottom: 4 }}>Combo 2</div>
                              <div style={{ maxHeight: 170, overflow: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                  <thead>
                                    <tr style={{ color: '#9aa3c7', textAlign: 'left' }}>
                                      <th style={{ ...stickyThStyle, padding: '4px 5px' }}>Combo</th>
                                      <th style={{ ...stickyThStyle, padding: '4px 5px', cursor: 'pointer' }} onClick={() => onPoolSort('combo', 'total')}>Pick{sortMark('combo', 'total')}</th>
                                      <th style={{ ...stickyThStyle, padding: '4px 5px', cursor: 'pointer' }} onClick={() => onPoolSort('combo', 'winRate')}>WR{sortMark('combo', 'winRate')}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {pkg.combo2Rows.length === 0 && (
                                      <tr>
                                        <td colSpan={3} style={{ padding: 6, color: '#9aa3c7' }}>ไม่มีข้อมูล</td>
                                      </tr>
                                    )}
                                    {pkg.combo2Rows.slice(0, 8).map((r) => (
                                      <tr key={`${pkg.team}-c2-${r.combo}`} style={{ borderTop: '1px solid rgba(255,255,255,.08)' }}>
                                        <td style={{ padding: '5px' }}>{renderComboCell(r.combo, 14)}</td>
                                        <td style={{ padding: '5px' }}>{r.total}</td>
                                        <td style={{ padding: '5px' }}>{r.winRate.toFixed(1)}%</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>

                            <div style={{ background: 'linear-gradient(180deg, rgba(32,7,12,.8), rgba(7,6,11,.94))', borderRadius: 8, padding: 6, border: '1px solid rgba(255,16,38,.18)' }}>
                              <div style={{ color: '#dbe4ff', fontWeight: 900, fontSize: 11, marginBottom: 4 }}>Combo 3</div>
                              <div style={{ maxHeight: 170, overflow: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                  <thead>
                                    <tr style={{ color: '#9aa3c7', textAlign: 'left' }}>
                                      <th style={{ ...stickyThStyle, padding: '4px 5px' }}>Combo</th>
                                      <th style={{ ...stickyThStyle, padding: '4px 5px', cursor: 'pointer' }} onClick={() => onPoolSort('combo', 'total')}>Pick{sortMark('combo', 'total')}</th>
                                      <th style={{ ...stickyThStyle, padding: '4px 5px', cursor: 'pointer' }} onClick={() => onPoolSort('combo', 'winRate')}>WR{sortMark('combo', 'winRate')}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {pkg.combo3Rows.length === 0 && (
                                      <tr>
                                        <td colSpan={3} style={{ padding: 6, color: '#9aa3c7' }}>ไม่มีข้อมูล</td>
                                      </tr>
                                    )}
                                    {pkg.combo3Rows.slice(0, 8).map((r) => (
                                      <tr key={`${pkg.team}-c3-${r.combo}`} style={{ borderTop: '1px solid rgba(255,255,255,.08)' }}>
                                        <td style={{ padding: '5px' }}>{renderComboCell(r.combo, 14)}</td>
                                        <td style={{ padding: '5px' }}>{r.total}</td>
                                        <td style={{ padding: '5px' }}>{r.winRate.toFixed(1)}%</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {opponentPoolByTeams.length > ALL_TEAMS_PAGE_SIZE && (
                      <div style={{ marginTop: 10, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <button
                          onClick={() => setAllTeamsPage((p) => Math.max(1, p - 1))}
                          disabled={allTeamsPage <= 1}
                          style={{
                            border: '1px solid rgba(255,255,255,.14)',
                            background: allTeamsPage <= 1 ? 'rgba(0,0,0,.20)' : 'rgba(255,16,38,.22)',
                            color: '#fff',
                            borderRadius: 999,
                            fontWeight: 800,
                            fontSize: 11,
                            padding: '4px 10px',
                            cursor: allTeamsPage <= 1 ? 'not-allowed' : 'pointer',
                            opacity: allTeamsPage <= 1 ? 0.5 : 1,
                          }}
                        >
                          ◀ ก่อนหน้า
                        </button>
                        <span style={{ color: '#9aa3c7', fontSize: 11 }}>
                          หน้า {allTeamsPage}/{totalAllTeamsPages}
                        </span>
                        <button
                          onClick={() => setAllTeamsPage((p) => Math.min(totalAllTeamsPages, p + 1))}
                          disabled={allTeamsPage >= totalAllTeamsPages}
                          style={{
                            border: '1px solid rgba(255,255,255,.14)',
                            background: allTeamsPage >= totalAllTeamsPages ? 'rgba(0,0,0,.20)' : 'rgba(255,16,38,.22)',
                            color: '#fff',
                            borderRadius: 999,
                            fontWeight: 800,
                            fontSize: 11,
                            padding: '4px 10px',
                            cursor: allTeamsPage >= totalAllTeamsPages ? 'not-allowed' : 'pointer',
                            opacity: allTeamsPage >= totalAllTeamsPages ? 0.5 : 1,
                          }}
                        >
                          ถัดไป ▶
                        </button>
                      </div>
                    )}
                    </div>
                    )}
                    </div>
                    </div>
                  </div>
                </div>
              </div>

                </>
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <button
                  onClick={handleUndo}
                  disabled={!history.length}
                  style={{
                    padding: '7px 24px',
                    borderRadius: 18,
                    border: `1px solid ${DRAFT_THEME.border}`,
                    background: DRAFT_THEME.surfaceStrong,
                    color: DRAFT_THEME.textStrong,
                    cursor: history.length ? 'pointer' : 'not-allowed',
                    opacity: history.length ? 1 : 0.5,
                  }}
                >
                  ↩ Undo
                </button>

                <strong style={{ fontSize: 21, color: 'var(--orange)', fontWeight: 900 }}>
                  {draftMode === 'free'
                    ? `Free Draft • Game ${currentGame}/${totalGames}`
                    : `Game ${currentGame}/${totalGames} • ${currentStep.type === 'ban' ? 'Ban' : 'Pick'} ${stepIndex + 1}/${totalSteps}`}
                </strong>

                <span style={{ color: DRAFT_THEME.text, opacity: 0.9, fontWeight: 700, fontSize: 12 }}>
                  {draftMode === 'sequence'
                    ? `คิว: ${currentStep?.team || '-'} • ${String(currentStep?.type || '-').toUpperCase()} x${currentStep?.count || 0}`
                    : `โหมดอิสระ • ${freeAction.toUpperCase()}`}
                </span>
              </div>
            </div>

            {banLayout === 'horizontal' && (
              <div
                className="fullsense-panel"
                style={{
                  margin: '10px 0 18px',
                  background: DRAFT_THEME.surface,
                  borderRadius: 12,
                  padding: 12,
                }}
              >
                <div style={{ display: 'grid', gridTemplateColumns: banSectionColumns, alignItems: 'stretch', gap: horizontalBanIsStacked ? 10 : 0 }}>
                  <div style={{ minWidth: 0, paddingRight: banSectionSidePadding }}>
                    <div style={{ color: 'var(--orange)', fontWeight: 900, marginBottom: 6, textAlign: 'left' }}>
                      {teamLabel(LEFT_TEAM_ID)} Bans
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-start', overflowX: 'auto' }}>
                      {renderSlots(bans[LEFT_TEAM_ID], 4, LEFT_TEAM_ID, 'ban')}
                    </div>
                  </div>
                  <div
                    style={{
                      position: 'relative',
                      display: horizontalBanIsStacked ? 'none' : 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <div
                      style={{
                        width: 3,
                        height: '92%',
                        background: 'linear-gradient(180deg, rgba(255,16,38,.45), rgba(255,16,38,.12))',
                        borderRadius: 2,
                      }}
                    />
                  </div>
                  <div style={{ minWidth: 0, paddingLeft: banSectionSidePadding }}>
                    <div style={{ color: 'var(--orange)', fontWeight: 900, marginBottom: 6, textAlign: horizontalBanIsStacked ? 'left' : 'right' }}>
                      {teamLabel(RIGHT_TEAM_ID)} Bans
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: banSectionSideJustify, overflowX: 'auto' }}>
                      {renderSlots(bans[RIGHT_TEAM_ID], 4, RIGHT_TEAM_ID, 'ban')}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Role filter + search */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '4px 0 16px' }}>
              {roleList.map((role) => (
                <button
                  key={role}
                  onClick={() => setSelectedRole(role)}
                  style={{
                    padding: '8px 23px',
                    borderRadius: 18,
                    border: `1px solid ${DRAFT_THEME.border}`,
                    background: selectedRole === role ? 'rgba(255,16,38,.14)' : DRAFT_THEME.surfaceStrong,
                    color: DRAFT_THEME.textStrong,
                    fontWeight: 900,
                    cursor: 'pointer',
                    boxShadow: selectedRole === role ? '0 0 12px rgba(255,16,38,.16)' : 'none',
                  }}
                >
                  {ROLE_ICON[role]} {role}
                </button>
              ))}
              <input
                type="text"
                placeholder="🔍 ค้นหา hero..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  padding: '0 16px',
                  height: 38,
                  borderRadius: 17,
                  border: `1px solid ${DRAFT_THEME.border}`,
                  background: DRAFT_THEME.surfaceStrong,
                  color: DRAFT_THEME.textStrong,
                  fontWeight: 700,
                  letterSpacing: 1,
                  ...filterSearchStyle,
                }}
              />
            </div>

            {/* Alert */}
            {alertMsg && (
              <div
                style={{
                  background: 'rgba(255,26,61,.12)',
                  color: DRAFT_THEME.textStrong,
                  padding: 14,
                  borderRadius: 10,
                  textAlign: 'center',
                  fontWeight: 'bold',
                  marginBottom: 14,
                  border: '1px solid rgba(255,26,61,.35)',
                  boxShadow: '0 0 18px rgba(255,26,61,.18)',
                }}
              >
                {alertMsg}
                <button
                  onClick={() => setAlertMsg('')}
                  style={{
                    marginLeft: 16,
                    background: 'transparent',
                    border: 'none',
                    color: DRAFT_THEME.textStrong,
                    cursor: 'pointer',
                    fontWeight: 900,
                  }}
                >
                  ×
                </button>
              </div>
            )}

            {/* Hero grids */}
            <div style={{ display: 'grid', gridTemplateColumns: heroGridColumns, gap: heroGridPairGap }}>
              {renderHeroGrid(LEFT_TEAM_ID)}
              {renderHeroGrid(RIGHT_TEAM_ID)}
            </div>

            {/* Next game */}
            {(stepIndex >= totalSteps || draftMode === 'free') && currentGame < totalGames && (
              <div style={{ textAlign: 'center', marginTop: 24 }}>
                <button
                  onClick={nextGame}
                  style={{
                    padding: '10px 30px',
                    borderRadius: 17,
                    border: 'none',
                    background: 'var(--orange)',
                    color: '#fff',
                    fontWeight: 900,
                    cursor: 'pointer',
                    fontSize: 17,
                    boxShadow: '0 0 18px var(--orange-glow)',
                  }}
                >
                  Next Game &gt;
                </button>
              </div>
            )}
          </div>

          {/* RIGHT PANEL */}
          <div
            className="fullsense-panel"
            style={{
              width: sidePanelWidth,
              background: panelColors[RIGHT_TEAM_ID],
              padding: isTabletViewport ? Math.max(8, sidePanelPadding - 1) : sidePanelPadding,
              boxShadow: DRAFT_THEME.shadow,
              borderRadius: '28px 0 0 28px',
              border: `2px solid ${highlightTeam === RIGHT_TEAM_ID ? highlightColor : DRAFT_THEME.border}`,
            }}
          >
            <h4 style={{ color: DRAFT_THEME.textStrong, textAlign: 'center' }}>
              {teamLabel(RIGHT_TEAM_ID)}
              {!weAreFirst && (
                <span
                  style={{
                    marginLeft: 6,
                    padding: '2px 6px',
                    borderRadius: 8,
                    background: 'var(--orange)',
                    color: '#fff',
                    fontWeight: 900,
                    fontSize: 11,
                    boxShadow: '0 0 12px var(--orange-glow)',
                  }}
                >
                  FS
                </span>
              )}
            </h4>

            {banLayout === 'vertical' && (
              <>
                <strong style={{ color: DRAFT_THEME.textStrong }}>Bans</strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
                  {renderSlots(bans[RIGHT_TEAM_ID], 4, RIGHT_TEAM_ID, 'ban')}
                </div>
              </>
            )}

            <strong style={{ color: PICK_TEXT_COLOR }}>Picks</strong>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
              {renderSlots(picks[RIGHT_TEAM_ID], 5, RIGHT_TEAM_ID, 'pick')}
            </div>
          </div>
        </div>

        {false && !isOpponentPoolOpen && (
          <button
            onClick={() => setIsOpponentPoolOpen(true)}
            style={{
              position: 'fixed',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 9300,
              border: `2px solid ${DRAFT_THEME.borderStrong}`,
              background: 'linear-gradient(180deg, #ff7b8f 0%, #ff3049 55%, #d91027 100%)',
              color: '#fff',
              borderRadius: '14px 0 0 14px',
              fontWeight: 900,
              fontSize: 13,
              letterSpacing: 0.35,
              padding: '12px 14px',
              cursor: 'pointer',
              boxShadow: '0 0 0 1px rgba(255,98,0,.35), 0 14px 34px rgba(0,0,0,.45), 0 0 28px rgba(255,120,20,.42)',
              textShadow: '0 1px 0 rgba(255,255,255,.35)',
            }}
          >
            ▶ Opponent Pool
          </button>
        )}
        {false && isOpponentPoolOpen && (
          <div
            onClick={() => setIsOpponentPoolOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,.34)',
              zIndex: 9300,
            }}
          />
        )}

{/* ===== SYNERGY COUNTER TOOLTIP ===== */}
{synergyTooltip && (
  <SynergyCounterTooltip
    hero={synergyTooltip.hero}
    teamPicks={picks[ourTeam]}
    enemyPicks={picks[enemyTeam]}
    allHeroes={heroes}
    position={{ x: synergyTooltip.x, y: synergyTooltip.y }}
    onClose={() => setSynergyTooltip(null)}
    getImage={getImage}
    synergyData={null}
    counterData={null}
  />
)}
        {/* ✅ Full Hero Intel Overlay */}
        {heroIntelPanel && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,.78)',
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 18,
            }}
            onClick={closeHeroIntel}
          >
            <div onClick={(e) => e.stopPropagation()}>
              <HeroIntelPanel
                hero={heroIntelPanel.hero}
                stats={heroIntelPanel.stats}
                perspective={heroIntelPanel.perspective}
                onClose={closeHeroIntel}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* =========================
   History (Lite)
   ========================= */
function HistoryView({ list, onBack, onLoad }) {
  const [selected, setSelected] = useState(null);

  if (!list || list.length === 0) {
    return (
      <div className="fullsense-root">
        <div className="fullsense-grid" />
        <div className="fullsense-stripe" />
        <div className="fullsense-content" style={{ padding: 40 }}>
          <button
            onClick={onBack}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: 12,
              background: 'var(--orange)',
              color: '#fff',
              fontWeight: 900,
              cursor: 'pointer',
              boxShadow: '0 0 14px var(--orange-glow)',
            }}
          >
            ← กลับ
          </button>
          <div style={{ marginTop: 16, opacity: 0.8, color: DRAFT_THEME.text }}>ยังไม่มีสแนปช็อตที่บันทึก</div>
        </div>
      </div>
    );
  }

  const data = selected || list[0];

  return (
    <div className="fullsense-root">
      <div className="fullsense-grid" />
      <div className="fullsense-stripe" />
      <div className="fullsense-content" style={{ padding: 30 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <button
            onClick={onBack}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: 12,
              background: 'var(--orange)',
              color: '#fff',
              fontWeight: 900,
              cursor: 'pointer',
              boxShadow: '0 0 14px var(--orange-glow)',
            }}
          >
            ← กลับ
          </button>
          <div style={{ fontWeight: 900, letterSpacing: 1, color: DRAFT_THEME.textStrong }}>History</div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 16 }}>
          <div className="fullsense-panel" style={{ background: DRAFT_THEME.surface, borderRadius: 14, padding: 12 }}>
            {list.map((snap, i) => (
              <div
                key={i}
                onClick={() => setSelected(snap)}
                style={{
                  padding: '10px 12px',
                  borderRadius: 10,
                  cursor: 'pointer',
                  background: data?.time === snap.time ? 'rgba(255,16,38,.12)' : 'transparent',
                  border: `1px solid ${DRAFT_THEME.border}`,
                  marginBottom: 8,
                  color: DRAFT_THEME.textStrong,
                }}
              >
                <div style={{ fontWeight: 900 }}>Game {snap.gameNo}</div>
                <div style={{ fontSize: 12, opacity: 0.85, color: DRAFT_THEME.text }}>BO: {snap.meta?.boType || '-'}</div>
              </div>
            ))}
          </div>

          <div className="fullsense-panel" style={{ background: DRAFT_THEME.surface, borderRadius: 14, padding: 16, minHeight: 280 }}>
            {data && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, color: DRAFT_THEME.textStrong }}>
                  <div style={{ fontWeight: 900, fontSize: 18 }}>Game {data.gameNo}</div>
                  <div style={{ opacity: 0.8, fontSize: 12, color: DRAFT_THEME.text }}>{new Date(data.time).toLocaleString()}</div>
                  <div style={{ marginLeft: 'auto' }}>
                    <button
                      onClick={() => onLoad?.(data)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 10,
                        border: 'none',
                        background: ACTION_COLOR.pick,
                        color: PICK_TEXT_COLOR,
                        fontWeight: 900,
                        cursor: 'pointer',
                      }}
                    >
                      Load to board
                    </button>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  {['A', 'B'].map((team) => (
                    <div key={team} style={{ background: DRAFT_THEME.surfaceStrong, borderRadius: 12, padding: 12, border: `1px solid ${DRAFT_THEME.border}` }}>
                      <div style={{ fontWeight: 900, marginBottom: 6, color: DRAFT_THEME.textStrong }}>Team {team}</div>

                      <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6, color: 'var(--orange)' }}>
                        Bans
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                        {(data.bans?.[team] || []).map((h, i) => (
                          <HistoryBubble key={team + '-b-' + i} hero={h} />
                        ))}
                      </div>

                      <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6, color: PICK_TEXT_COLOR }}>
                        Picks
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {(data.picks?.[team] || []).map((h, i) => (
                          <HistoryBubble key={team + '-p-' + i} hero={h} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function HistoryBubble({ hero }) {
  if (!hero) return null;
  return (
    <div
      title={hero.name}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 8px',
        borderRadius: 10,
        background: 'rgba(255,255,255,.06)',
        border: '1px solid rgba(255,255,255,.12)',
        color: '#fff',
      }}
    >
      <img
        src={getImage(hero.image)}
        alt={hero.name}
        style={{ width: 22, height: 22, borderRadius: 6, objectFit: 'cover' }}
      />
      <span style={{ fontSize: 12, fontWeight: 800 }}>{hero.name}</span>
    </div>
  );
}
